/**
 * ChartPanel — single chart pane backed by lightweight-charts.
 *
 * Each panel is fully independent: symbol, timeframe, chart type, drawings,
 * indicators, and backfill state are all scoped to the panel's `panelId`.
 *
 * Key responsibilities:
 * - Creates / destroys the `IChartApi` instance (lightweight-charts)
 * - Subscribes to tick data from Redux and converts it to OHLCV candles via
 *   `chart-data-handler`
 * - Runs the two-phase backfill (today + 60-day history) via
 *   `chart-backfill-handler`
 * - Manages per-panel drawings (persisted to localStorage per symbol)
 * - Hosts `IndicatorRenderer` for overlay / sub-pane indicators
 * - Hosts sub-panels: `DrawingOverlay`, `PriceAlertPanel`, `StrategyPanel`,
 *   `CustomIndicatorPanel`, `BarReplayPanel`
 * - Exposes an imperative `ChartPanelHandle` ref so `Chart.tsx` can forward
 *   toolbar actions (zoom, screenshot, add/remove indicator, etc.)
 * - Supports drag-and-drop symbol assignment via `dragover`/`drop` events
 */
import React, { useEffect, useRef, useState, useCallback, useMemo, useImperativeHandle, forwardRef } from "react";
import { createChart, IChartApi, UTCTimestamp, CandlestickSeries, LineSeries, AreaSeries, BaselineSeries, BarSeries } from "lightweight-charts";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import html2canvas from "html2canvas";
import { ChartPanel as ChartPanelType, TimeframeType, TIMEFRAME_CONFIGS, getTimeframeConfig, registerCustomTimeframe, ChartSymbol, CandleData, ChartType, CHART_TYPE_LABELS, OneMinuteBar, DayBar, isDayBasedTimeframe } from "../../types/chart.types";
import { setSymbol, setTimeframe, setChartType, removePanel, setPanelError, clearPanelError, prependHistoryData, prependEodData, gateHistoryLoading, markHistoryLoaded, clearAllEodData, setCrosshairTime, setActiveTab, addCompareSymbol, removeCompareSymbol } from "../../store/chart-slice";
import { ScripState } from "../../store/watchlist-slice";
import MarketSegmentModal from "../market-segment-modal/MarketSegmentModal";
import { chartDataHandler } from "../../service/chart-data-handler";
import { chartBackfillHandler } from "../../service/chart-backfill-handler";
import { getColorTheme, loadDrawings, loadChartSettings, SavedChartSettings, getDefaultTemplate, loadCustomTimeframes, loadBackfillDays, loadEodDuration, loadChartBgPreset, loadPaneBgPreset, savePaneBg, clearPaneBg, CHART_BG_PRESETS, ChartBgPreset } from "@service/local-storage-handler";
import { syncSaveDrawings } from "@service/sync-handler";
import { IndicatorRenderer } from "../../indicators/renderer/indicator-renderer";
import { indicatorEngine } from "../../indicators/engine/indicator-engine";
import { ActiveIndicator, IndicatorDefinition } from "../../indicators/types/indicator.types";
import { Drawing, DrawingTool } from "../../types/drawing.types";
import IndicatorPanel from "./IndicatorPanel";
import DrawingOverlay from "./DrawingOverlay";
import PriceAlertPanel from "./PriceAlertPanel";
import StrategyPanel from "./StrategyPanel";
import CustomIndicatorPanel from "./CustomIndicatorPanel";
import BarReplayPanel from "./BarReplayPanel";
import CompareSymbolPanel, { CompareEntry } from "./CompareSymbolPanel";
import AlphaTradeOverlay from "./AlphaTradeOverlay";
import { AlphaTradeDashboard } from "../../indicators/built-in/alpha-trade";
import ProfitMachineOverlay from "./ProfitMachineOverlay";
import { ProfitMachineDashboard } from "../../indicators/built-in/profit-machine";
import ScalpingTrendOverlay from "./ScalpingTrendOverlay";
import { ScalpingTrendDashboard } from "../../indicators/built-in/scalping-trend";
import PrimeMagicOverlay from "./PrimeMagicOverlay";
import { PrimeMagicDashboard } from "../../indicators/built-in/prime-magic";
import MaxWealthOverlay from "./MaxWealthOverlay";
import { MaxWealthDashboard } from "../../indicators/built-in/max-wealth";
import SwingTradeOverlay from "./SwingTradeOverlay";
import { SwingTradeDashboard } from "../../indicators/built-in/swing-trade";
import TrendRoverOverlay from "./TrendRoverOverlay";
import { TrendRoverDashboard } from "../../indicators/built-in/trend-rover";
import TrendMonsterOverlay from "./TrendMonsterOverlay";
import { TrendMonsterDashboard } from "../../indicators/built-in/trend-monster";
import TradeMintOverlay from "./TradeMintOverlay";
import { TradeMintDashboard } from "../../indicators/built-in/trade-mint";
import PriceActionOverlay from "./PriceActionOverlay";
import { PriceActionDashboard } from "../../indicators/built-in/price-action";
import AlphaTradeV2Overlay from "./AlphaTradeV2Overlay";
import { AlphaTradeV2Dashboard } from "../../indicators/built-in/alpha-trade-v2";
import ShadowBreakerOverlay from "./ShadowBreakerOverlay";
import { ShadowBreakerDashboard } from "../../indicators/built-in/shadow-breaker";
import ActiveIndicatorsBar from "./ActiveIndicatorsBar";
import { inferTickSize } from "../../indicators/built-in/price-utils";
import { PriceAlert as PriceAlertType, addNotification } from "../../store/alert-slice";
import { isMobile, isDesktop } from "@platform";
import toast from "react-hot-toast";
import "./ChartPanel.scss";

// Stable empty array references to prevent re-renders from new [] allocations
const EMPTY_ARRAY: any[] = [];
const EMPTY_CANDLES: any[] = [];

// Cross-panel dedupe for strategy alerts: if the same strategy event is
// produced by multiple chart panels (same symbol + timeframe), emit once.
const crossPanelStrategyAlertSeen = new Map<string, number>();
const CROSS_PANEL_ALERT_TTL_MS = 6 * 60 * 60 * 1000;
const CROSS_PANEL_ALERT_MAX_KEYS = 5000;

const shouldEmitCrossPanelStrategyAlert = (eventKey: string): boolean => {
  const now = Date.now();
  const lastSeen = crossPanelStrategyAlertSeen.get(eventKey);
  if (lastSeen != null && (now - lastSeen) < CROSS_PANEL_ALERT_TTL_MS) {
    return false;
  }

  crossPanelStrategyAlertSeen.set(eventKey, now);

  if (crossPanelStrategyAlertSeen.size > CROSS_PANEL_ALERT_MAX_KEYS) {
    for (const [key, ts] of crossPanelStrategyAlertSeen) {
      if ((now - ts) >= CROSS_PANEL_ALERT_TTL_MS) {
        crossPanelStrategyAlertSeen.delete(key);
      }
    }

    if (crossPanelStrategyAlertSeen.size > CROSS_PANEL_ALERT_MAX_KEYS) {
      const removeCount = crossPanelStrategyAlertSeen.size - CROSS_PANEL_ALERT_MAX_KEYS;
      let removed = 0;
      for (const key of crossPanelStrategyAlertSeen.keys()) {
        crossPanelStrategyAlertSeen.delete(key);
        removed += 1;
        if (removed >= removeCount) break;
      }
    }
  }

  return true;
};

const withAlpha = (hexColor: string, alphaHex: string): string => `${hexColor}${alphaHex}`;

// Register any saved custom timeframes at module load so convertTimeframe works
loadCustomTimeframes().forEach(ct => registerCustomTimeframe(ct.key, { minutes: ct.minutes, label: ct.label }));

interface ChartPanelProps {
  panelId: string;
  availableSymbols: ChartSymbol[];
  // Global drawing tool state (lifted from per-pane to parent)
  activeTool: DrawingTool;
  activeDrawingColor: string;
  magnetEnabled: boolean;
  drawingsVisible: boolean;
  showActiveIndicators?: boolean;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onMagnetToggle: () => void;
  onToggleDrawingsVisibility: () => void;
  isActive?: boolean;
  isTabLayout?: boolean;
}

/** Imperative handle exposed to the parent (Chart.tsx) */
export interface ChartPanelHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  scrollToLatest: () => void;
  takeScreenshot: () => void;
  addIndicator: (id: string) => void;
  removeIndicator: (id: string) => void;
  removeAllIndicators: () => void;
  toggleIndicatorVisibility: (id: string) => void;
  changeIndicatorParams: (id: string, params: Record<string, any>) => void;
  getActiveIndicators: () => ActiveIndicator[];
  getVisibleBarsCount: () => number;
  getDrawingCount: () => number;
  clearAllDrawings: () => void;
  undoDrawing: () => void;
  toggleAlerts: () => void;
  toggleStrategy: () => void;
  toggleCustomIndicator: () => void;
  toggleReplay: () => void;
  toggleCompare: () => void;
  toggleGrid: (visible: boolean) => void;
  applyCustomIndicator: (def: IndicatorDefinition) => void;
  applyTemplate: (template: { chartType: string; indicators: Array<{ indicatorId: string; params: Record<string, any>; visible: boolean }>; visibleBarsCount?: number }) => void;
}

const ChartPanel = React.memo(forwardRef<ChartPanelHandle, ChartPanelProps>(({ panelId, availableSymbols, activeTool, activeDrawingColor, magnetEnabled, drawingsVisible, showActiveIndicators = true, onToolChange, onColorChange, onMagnetToggle, onToggleDrawingsVisibility, isActive, isTabLayout }, ref) => {
  const dispatch = useDispatch();
  const chartPanelRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<any>(null);
  // Track how many candles were last rendered so we know setData vs update
  const lastCandleCountRef = useRef<number>(0);
  const lastCandlesRef = useRef<CandleData[]>([]);
  // Persistent candle color map from active strategy indicator.
  // Keyed by IST-adjusted time → color string. Survives between live ticks
  // so that individual .update() calls can preserve custom candle colors.
  const candleColorMapRef = useRef<Map<number, string>>(new Map());
  // Tracks which strategy owns the color map so inactive strategies don't
  // wipe colors set by another active strategy.
  const candleColorOwnerRef = useRef<string | null>(null);
  // Bumped by apply*CandleColors when the color map actually changes.
  // Added to main render effect deps so it triggers a full setData redraw.
  const [colorMapVersion, setColorMapVersion] = useState(0);
  // Companion flag: when true the main render effect forces the full setData
  // path (skipping the incremental update shortcut) so all candles get recolored.
  const needsColorRedrawRef = useRef(false);
  // Incremented every time the chart instance is (re)created so that the
  // data-render effect re-fires even when Redux data hasn't changed.
  const [chartVersion, setChartVersion] = useState(0);
  // Track last rendered timeframe to force full setData on timeframe switch
  const lastRenderedTimeframeRef = useRef<string>("");
  // True when user has manually scrolled the time axis away from the latest bar.
  // When false, live updates auto-scroll the chart to keep the latest candle visible.
  const isUserScrolledAwayRef = useRef(false);
  // User's preferred number of empty bars between the latest candle and the right
  // chart edge. Defaults to 5. Updated whenever the user drags the X-axis while
  // viewing the latest candle — live ticks preserve this value instead of
  // always snapping back to a hardcoded offset.
  const userRightOffsetRef = useRef(5);
  // Set to true around our own scrollToPosition calls so the range-change
  // listener does not misinterpret them as user-driven X-axis drags.
  const isProgrammaticScrollRef = useRef(false);
  // True after the first fitContent() for the current symbol — subsequent
  // full setData() calls (backfill, merge) preserve the user's zoom/scroll.
  const hasInitialFitRef = useRef<boolean>(false);
  // Track pending positioning requestAnimationFrame so we can cancel it on
  // unmount or when new data arrives before the frame fires.
  const positionRAFRef = useRef<number | null>(null);
  // Local gate: true while triggerFetch / triggerEodFetch async is in-flight.
  // The render effect skips chart updates while this is true, then renders
  // once with the complete dataset when the fetch completes.  Unlike the
  // old Redux-based isHistoryLoading gate, a local ref can never get stuck
  // across effect orderings or timeframe switches.
  const isFetchingRef = useRef(false);

  // ─── Indicator System ──────────────────────────────────────────
  const indicatorRendererRef = useRef<IndicatorRenderer | null>(null);
  // Lazy-initialize to avoid creating throwaway instances in React strict mode
  if (!indicatorRendererRef.current) {
    indicatorRendererRef.current = new IndicatorRenderer();
  }
  const [activeIndicators, setActiveIndicators] = useState<ActiveIndicator[]>([]);
  // Debounce timer for indicator param changes
  const indicatorUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether a full indicator recalc is needed (vs live-tick update)
  const indicatorNeedsFullRecalcRef = useRef(false);

  // ─── Drawing Tools System ──────────────────────────────────────
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  // activeTool, activeDrawingColor, magnetEnabled, drawingsVisible come from props now
  // Track the symbol+timeframe key for drawing persistence
  const drawingKeyRef = useRef<string>("");



  // ─── Price Alert Panel ──────────────────────────────────────
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);

  // ─── Strategy / Backtest Panel ──────────────────────────────
  const [isStrategyOpen, setIsStrategyOpen] = useState(false);

  // ─── Custom Indicator Scripting ──────────────────────────────
  const [isCustomIndicatorOpen, setIsCustomIndicatorOpen] = useState(false);

  // ─── Bar Replay Mode ──────────────────────────────────────
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayBar, setReplayBar] = useState(0);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1000); // ms per bar
  const replayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Compare Symbol Overlay ─────────────────────────────────
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [compareEntries, setCompareEntries] = useState<CompareEntry[]>([]);
  const compareColorIndexRef = useRef(0);
  const compareAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const replayAllCandlesRef = useRef<CandleData[]>([]);

  // ─── Per-Pane Background Color ────────────────────────────────
  const [paneBgPickerOpen, setPaneBgPickerOpen] = useState(false);
  const [paneBgId, setPaneBgId] = useState<string | null>(() => {
    const saved = loadPaneBgPreset(panelId);
    return saved ? saved.id : null;
  });
  const paneBgBtnRef = useRef<HTMLButtonElement>(null);

  // ─── Pane Right-Click Context Menu (desktop only) ─────────────
  const [paneContextMenu, setPaneContextMenu] = useState<{ x: number; y: number } | null>(null);

  const handlePaneContextMenu = useCallback((e: React.MouseEvent) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
    setPaneContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    if (!paneContextMenu) return;
    const close = () => setPaneContextMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [paneContextMenu]);

  // ─── Paginated Backfill State ─────────────────────────────────
  // Tracks scroll-based lazy loading of historical pages (30 days per page)
  const backfillPageRef = useRef(1);          // last successfully loaded page
  const backfillHasMoreRef = useRef(true);    // server has more pages
  const backfillLoadingRef = useRef(false);   // prevents concurrent page fetches
  const backfillDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backfillMaxPageRef = useRef(Math.ceil(loadBackfillDays() / 30)); // max pages from user setting
  // Tracks which symbol has had backfill triggered this session so that a
  // timeframe switch from day-based (1D) to intraday (1M, 5M…) correctly
  // fires a fresh fetch even when a few live-tick bars already exist.
  const backfillFetchedForSymbolRef = useRef<string>("");

  // ─── Alpha Trade Dashboard ───────────────────────────────────
  const [alphaTradeData, setAlphaTradeData] = useState<AlphaTradeDashboard | null>(null);
  const [atDashboardVisible, setAtDashboardVisible] = useState(true);
  const [atArrowBadges, setAtArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short"; type: string; position: string }[]>([]);
  const atAlertContextRef = useRef<string>("");
  const atAlertInitializedRef = useRef(false);
  const atSeenEventKeysRef = useRef<Set<string>>(new Set());
  const atAlertBaselineTimeRef = useRef<number | null>(null);
  // ─── Profit Machine Dashboard ────────────────────────────────
  const [profitMachineData, setProfitMachineData] = useState<ProfitMachineDashboard | null>(null);
  const [pmDashboardVisible, setPmDashboardVisible] = useState(true);
  const [pmArrowBadges, setPmArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Scalping Trend Dashboard ────────────────────────────────
  const [scalpingTrendData, setScalpingTrendData] = useState<ScalpingTrendDashboard | null>(null);
  const [stDashboardVisible, setStDashboardVisible] = useState(true);
  const [stArrowBadges, setStArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Prime Magic Dashboard ───────────────────────────────────
  const [primeMagicData, setPrimeMagicData] = useState<PrimeMagicDashboard | null>(null);
  const [pmgDashboardVisible, setPmgDashboardVisible] = useState(true);
  const [pmgArrowBadges, setPmgArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Max Wealth Dashboard ────────────────────────────────────
  const [maxWealthData, setMaxWealthData] = useState<MaxWealthDashboard | null>(null);
  const [mwDashboardVisible, setMwDashboardVisible] = useState(true);
  const [mwArrowBadges, setMwArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Shadow Breaker Dashboard ────────────────────────────────
  const [shadowBreakerData, setShadowBreakerData] = useState<ShadowBreakerDashboard | null>(null);
  const [sbDashboardVisible, setSbDashboardVisible] = useState(true);
  const [sbArrowBadges, setSbArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Swing Trade Dashboard ───────────────────────────────────
  const [swingTradeData, setSwingTradeData] = useState<SwingTradeDashboard | null>(null);
  const [swDashboardVisible, setSwDashboardVisible] = useState(true);
  const [swArrowBadges, setSwArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Trend Rover Dashboard ───────────────────────────────────
  const [trendRoverData, setTrendRoverData] = useState<TrendRoverDashboard | null>(null);
  const [trDashboardVisible, setTrDashboardVisible] = useState(true);
  const [trArrowBadges, setTrArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short"; type?: string; position?: string }[]>([]);
  // ─── Trend Monster Dashboard ─────────────────────────────────
  const [trendMonsterData, setTrendMonsterData] = useState<TrendMonsterDashboard | null>(null);
  const [tmnDashboardVisible, setTmnDashboardVisible] = useState(true);
  const [tmnArrowBadges, setTmnArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short"; type: string; position: string }[]>([]);
  // ─── Trade Mint Dashboard ────────────────────────────────────
  const [tradeMintData, setTradeMintData] = useState<TradeMintDashboard | null>(null);
  const [tmDashboardVisible, setTmDashboardVisible] = useState(true);
  const [tmArrowBadges, setTmArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Price Action Dashboard ──────────────────────────────────
  const [priceActionData, setPriceActionData] = useState<PriceActionDashboard | null>(null);
  const [paDashboardVisible, setPaDashboardVisible] = useState(true);
  const [paArrowBadges, setPaArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short" }[]>([]);
  // ─── Alpha Trade V2 Dashboard ────────────────────────────────
  const [alphaTradeV2Data, setAlphaTradeV2Data] = useState<AlphaTradeV2Dashboard | null>(null);
  const [atv2DashboardVisible, setAtv2DashboardVisible] = useState(true);
  const [atv2ArrowBadges, setAtv2ArrowBadges] = useState<{ x: number; y: number; direction: "long" | "short"; type: string; position: string }[]>([]);
  const atv2AlertContextRef = useRef<string>("");
  const atv2AlertInitializedRef = useRef(false);
  const atv2SeenEventKeysRef = useRef<Set<string>>(new Set());
  const atv2AlertBaselineTimeRef = useRef<number | null>(null);
  const strategyAlertContextRef = useRef<Map<string, string>>(new Map());
  const strategyAlertInitializedRef = useRef<Map<string, boolean>>(new Map());
  const strategyAlertSeenRef = useRef<Map<string, Set<string>>>(new Map());
  const strategyAlertBaselineRef = useRef<Map<string, number>>(new Map());
  const strategyAlertLastStatusRef = useRef<Map<string, string>>(new Map());

  const handleDrawingComplete = useCallback((drawing: Drawing) => {
    setDrawings(prev => [...prev, drawing]);
    // Keep the tool active for rapid multi-draw; user clicks cursor or presses Escape to stop
  }, []);

  const handleDrawingUpdate = useCallback((updated: Drawing) => {
    setDrawings(prev => prev.map(d => d.id === updated.id ? updated : d));
  }, []);

  const handleDrawingDelete = useCallback((id: string) => {
    setDrawings(prev => prev.filter(d => d.id !== id));
  }, []);

  const handleClearAllDrawings = useCallback(() => {
    setDrawings([]);
  }, []);

  const handleUndoDrawing = useCallback(() => {
    setDrawings(prev => prev.slice(0, -1));
  }, []);

  // ─── Drag-and-Drop from Market Watch ──────────────────────────
  //
  // Strategy: render a transparent full-coverage overlay div on this pane
  // whenever a chart-symbol drag is in progress. This overlay sits above the
  // lightweight-charts canvas and DrawingOverlay SVG (both of which can
  // silently swallow drag events), so drop events are guaranteed to land here.
  //
  const [isSymbolDragActive, setIsSymbolDragActive] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    const onDragStart = () => setIsSymbolDragActive(true);
    const onDragEnd = () => {
      setIsSymbolDragActive(false);
      setIsDragOver(false);
    };
    window.addEventListener("chart-symbol-drag-start", onDragStart);
    window.addEventListener("chart-symbol-drag-end", onDragEnd);
    window.addEventListener("chart-symbol-dropped", onDragEnd);
    return () => {
      window.removeEventListener("chart-symbol-drag-start", onDragStart);
      window.removeEventListener("chart-symbol-drag-end", onDragEnd);
      window.removeEventListener("chart-symbol-dropped", onDragEnd);
    };
  }, []);

  const handleOverlayDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  }, []);

  const handleOverlayDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  }, []);

  const handleOverlayDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleOverlayDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setIsSymbolDragActive(false);

    // Read payload: primary MIME → text/plain fallback → window data
    let raw = e.dataTransfer.getData("application/chart-symbol");
    if (!raw) {
      const plain = e.dataTransfer.getData("text/plain");
      if (plain && plain.startsWith("chart-symbol:")) {
        raw = plain.slice("chart-symbol:".length);
      }
    }
    if (!raw) {
      raw = (window as any).__chartSymbolData || "";
    }
    if (!raw) return;

    try {
      const sym: ChartSymbol = JSON.parse(raw);
      dispatch(setSymbol({ panelId, symbol: sym }));
      window.dispatchEvent(new Event("chart-symbol-dropped"));
    } catch {}

    (window as any).__chartSymbolDrag = false;
    (window as any).__chartSymbolData = null;
  }, [dispatch, panelId]);

  // ─── Drawing Persistence (moved after panel selector) ────────

  const handleZoomIn = useCallback(() => {
    if (!chartRef.current) return;
    const ts = chartRef.current.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;
    const center = (range.from + range.to) / 2;
    const quarter = (range.to - range.from) / 4;
    ts.setVisibleLogicalRange({ from: center - quarter, to: center + quarter });
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!chartRef.current) return;
    const ts = chartRef.current.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;
    const center = (range.from + range.to) / 2;
    const half = (range.to - range.from);
    ts.setVisibleLogicalRange({ from: center - half, to: center + half });
  }, []);

  const handleToggleDrawingsVisibility = useCallback(() => {
    onToggleDrawingsVisibility();
  }, [onToggleDrawingsVisibility]);

  // IST offset constant — also used by drawing overlay for coordinate conversion
  const IST_OFFSET = 19800;

  // OHLC legend state — shows data for the candle under the crosshair
  interface LegendData {
    time: number; // unix timestamp (seconds)
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    oi: number;
    change: number; // close - open
  }
  const [legendData, setLegendData] = useState<LegendData | null>(null);

  const themeFromStore = useSelector(({ theme }) => theme.theme);
  const colorTheme = themeFromStore || getColorTheme() || "dark";

  // Watchlist scrips for the empty-state symbol grid
  const watchlistScrips: ScripState[] = useSelector((state: any) => state.watchlist?.scrips || EMPTY_ARRAY);
  const [addSymbolModalOpen, setAddSymbolModalOpen] = useState(false);

  // Memoized selectors: avoid new references on every Redux update
  const panel = useSelector(
    (state: any) => (state.chart?.panels || []).find((p: ChartPanelType) => p.id === panelId),
    // Custom equality: only re-render when panel fields we care about change
    (prev: any, next: any) => {
      if (prev === next) return true;
      if (!prev || !next) return false;
      return prev.id === next.id
        && prev.symbol?.symbol === next.symbol?.symbol
        && prev.symbol?.id === next.symbol?.id
        && prev.timeframe === next.timeframe
        && prev.chartType === next.chartType
        && prev.isLoading === next.isLoading
        && prev.error === next.error;
    }
  );

  // Extract symbol name to use in dependent selector
  const panelSymbolName = panel?.symbol?.symbol || null;

  // Keep a ref for the current panel symbol so the scroll callback
  // (created once during chart init) always reads the latest value
  // instead of a stale closure capture.
  const panelSymbolRef = useRef(panel?.symbol);
  panelSymbolRef.current = panel?.symbol;

  const oneMinuteData = useSelector((state: any) => {
    if (panelSymbolName) {
      return state.chart?.oneMinuteData?.[panelSymbolName] || EMPTY_ARRAY;
    }
    return EMPTY_ARRAY;
  // Reference equality: the array reference in Redux only changes when
  // a bar is actually added/updated for this specific symbol
  });

  // EOD (end-of-day) day bars — populated by the EOD backfill for day-based timeframes
  const eodData: DayBar[] = useSelector((state: any) => {
    if (panelSymbolName) {
      return state.chart?.eodData?.[panelSymbolName] || EMPTY_ARRAY;
    }
    return EMPTY_ARRAY;
  });

  // Version counter — bumped when EOD duration changes, forces all panels to re-fetch
  const eodVersion: number = useSelector((state: any) => state.chart?.eodVersion ?? 0);
  const alertSoundEnabled: boolean = useSelector((state: any) => state.alerts?.alertSoundEnabled ?? true);

  // ─── Compare Symbol Live Tick Subscription ────────────────────
  // Memoised list of compare symbol names — only re-computes when the user
  // adds or removes a compare symbol (i.e., compareEntries changes).
  const compareSymbolNames = useMemo(
    () => compareEntries.map(e => e.symbol.symbol),
    [compareEntries],
  );

  // Subscribe to the *last bar* for each compare symbol in Redux oneMinuteData.
  // shallowEqual ensures we only re-render when an actual bar object changes
  // (reference equality), not on every unrelated Redux dispatch.
  const compareLastBars = useSelector(
    (state: any): Record<string, OneMinuteBar | null> => {
      if (compareSymbolNames.length === 0) return {};
      const result: Record<string, OneMinuteBar | null> = {};
      for (const name of compareSymbolNames) {
        const bars = state.chart?.oneMinuteData?.[name];
        result[name] = bars && bars.length > 0 ? bars[bars.length - 1] : null;
      }
      return result;
    },
    shallowEqual,
  );


  // ─── Drawing Persistence ──────────────────────────────────────
  // Save drawings to localStorage whenever they change
  useEffect(() => {
    if (!panel?.symbol?.symbol || !panel?.timeframe) return;
    const key = `${panel.symbol.symbol}_${panel.timeframe}`;
    drawingKeyRef.current = key;
    syncSaveDrawings(panel.symbol.symbol, panel.timeframe, drawings);
  }, [drawings, panel?.symbol?.symbol, panel?.timeframe]);

  // Load drawings when symbol or timeframe changes
  useEffect(() => {
    if (!panel?.symbol?.symbol || !panel?.timeframe) return;
    const key = `${panel.symbol.symbol}_${panel.timeframe}`;
    if (key !== drawingKeyRef.current) {
      drawingKeyRef.current = key;
      const saved = loadDrawings(panel.symbol.symbol, panel.timeframe);
      setDrawings(saved);
    }
  }, [panel?.symbol?.symbol, panel?.timeframe]);

  // Abort controller ref to cancel background fetches on symbol change
  const abortRef = useRef<AbortController | null>(null);

  /**
   * Load the next page of historical backfill data.
   * After each successful page, auto-continues to the next page (with a
   * 1-second delay) until the server signals no more data or the symbol
   * changes.  Uses only refs for state so it is safe to call from any
   * closure (useEffect, setTimeout, etc.).
   */
  const loadNextBackfillPageRef = useRef<() => void>(() => {});
  loadNextBackfillPageRef.current = () => {
    if (!backfillHasMoreRef.current || backfillLoadingRef.current) return;
    const sym = panelSymbolRef.current;
    if (!sym?.id || !sym?.symbol) return;
    if (abortRef.current?.signal.aborted) return;

    const nextPage = backfillPageRef.current + 1;

    // Honour the user's backfill history limit (each page = 30 days)
    if (nextPage > backfillMaxPageRef.current) {
      backfillHasMoreRef.current = false;
      console.log(`[Chart] Backfill limit reached (${backfillMaxPageRef.current} pages = ${backfillMaxPageRef.current * 30} days) for ${sym.symbol}`);
      return;
    }

    backfillLoadingRef.current = true;
    console.log(`[Chart] Loading backfill page ${nextPage} for ${sym.symbol}`);

    chartBackfillHandler.fetchNextPage(sym.id, sym.symbol, nextPage)
      .then(({ bars, hasMore }) => {
        if (abortRef.current?.signal.aborted) return;
        if (bars.length > 0) {
          dispatch(prependHistoryData({ symbol: sym.symbol, bars }));
        }
        backfillPageRef.current = nextPage;
        backfillHasMoreRef.current = hasMore;
        console.log(`[Chart] Page ${nextPage} loaded: ${bars.length} bars, hasMore=${hasMore}`);

        // Auto-continue: only schedule next page if this page had data AND server has more
        if (hasMore && bars.length > 0 && !abortRef.current?.signal.aborted) {
          backfillDebounceRef.current = setTimeout(() => {
            loadNextBackfillPageRef.current();
          }, 2000);
        }
      })
      .catch((err) => {
        if (!abortRef.current?.signal.aborted) {
          console.warn(`[Chart] Page ${nextPage} fetch failed:`, err.message);
        }
      })
      .finally(() => {
        backfillLoadingRef.current = false;
      });
  };

  /**
   * Background fetch — does NOT block chart rendering.
   * Live ticks render immediately via websocket middleware.
   * Phase 1: todayfill (morning till previous minute) merges behind live ticks
   * Phase 2: paginated backfill — page 1 loads, then auto-continues all
   *          remaining pages in the background (1-second delay between pages)
   */
  const triggerFetch = (sym: typeof panel.symbol) => {
    if (!sym?.symbol) return;

    // Cancel any previous background fetch for this panel
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Reset pagination state for the new symbol
    backfillPageRef.current = 1;
    backfillHasMoreRef.current = true;
    backfillLoadingRef.current = false;
    backfillMaxPageRef.current = 1; // server returns all history in one shot — no pagination
    backfillFetchedForSymbolRef.current = sym.symbol; // mark fetch as started for this symbol
    if (backfillDebounceRef.current) {
      clearTimeout(backfillDebounceRef.current);
      backfillDebounceRef.current = null;
    }

    lastCandleCountRef.current = 0;
    lastCandlesRef.current = [];
    lastRenderedTimeframeRef.current = "";
    hasInitialFitRef.current = false;
    // Reset scroll tracking so the new symbol auto-follows the latest candle.
    isUserScrolledAwayRef.current = false;
    // Reset right-side offset to default so the new symbol starts with normal spacing.
    userRightOffsetRef.current = 5;
    // Gate live ticks: buffer WebSocket ticks while todayfill + backfill load.
    // Once markHistoryLoaded fires, buffered ticks flush into oneMinuteData.
    isFetchingRef.current = true;
    dispatch(gateHistoryLoading({ symbol: sym.symbol }));
    if (candleSeriesRef.current) {
      try { candleSeriesRef.current.setData([]); } catch (_) {}
    }
    // Re-enable auto-scale so the price axis adjusts to the new symbol's range
    // (manual price axis interaction disables it, and it stays off across setData calls)
    if (chartRef.current) {
      try {
        chartRef.current.priceScale("right").applyOptions({ autoScale: true });
        chartRef.current.priceScale("left").applyOptions({ autoScale: true });
        // Reset chart's rightOffset to match the ref so shiftVisibleRangeOnNewBar
        // uses the correct value for the new symbol
        chartRef.current.timeScale().applyOptions({ rightOffset: userRightOffsetRef.current });
      } catch (_) {}
    }
    dispatch(clearPanelError(panelId));

    const scripId = sym.id;
    const symbolName = sym.symbol;

    if (!scripId) {
      dispatch(setPanelError({ panelId, error: `Symbol ID not found for ${symbolName} — cannot fetch history` }));
      return;
    }

    // ── Phase 1: todayfill (fast — morning candles till previous minute) ──
    (async () => {
      try {
        const todayBars = await chartBackfillHandler.fetchTodayData(scripId, symbolName);
        if (controller.signal.aborted) return;

        if (todayBars.length > 0) {
          dispatch(prependHistoryData({ symbol: symbolName, bars: todayBars }));
        }
      } catch (error: any) {
        if (controller.signal.aborted) return;
        console.warn(`[Chart] Todayfill failed for ${symbolName}:`, error.message);
        // Non-critical — live ticks are already rendering
      }

      // ── Phase 2: paginated backfill — page 1 (30 days) ─────────
      try {
        const { bars: historyBars, hasMore } = await chartBackfillHandler.fetchHistoryData(scripId, symbolName);
        if (controller.signal.aborted) return;

        if (historyBars.length > 0) {
          dispatch(prependHistoryData({ symbol: symbolName, bars: historyBars }));
        }
        backfillPageRef.current = 1;
        backfillHasMoreRef.current = hasMore;

        // Auto-start loading remaining pages in the background (within user's day limit)
        if (hasMore && historyBars.length > 0 && !controller.signal.aborted && backfillMaxPageRef.current > 1) {
          backfillDebounceRef.current = setTimeout(() => {
            loadNextBackfillPageRef.current();
          }, 2000);
        }
      } catch (error: any) {
        if (!controller.signal.aborted) {
          console.warn(`[Chart] Background backfill failed for ${symbolName}:`, error.message);
          backfillHasMoreRef.current = false;
        }
      }

      // ── History loaded — ungate live ticks ──────────────────────
      // Flush any buffered WebSocket ticks into oneMinuteData and let
      // future ticks flow directly.  Runs whether phases above succeeded
      // or failed so the chart never stays permanently gated.
      if (!controller.signal.aborted) {
        dispatch(markHistoryLoaded({ symbol: symbolName }));
        isFetchingRef.current = false;
      }
    })();
  };

  /**
   * Fetch EOD (end-of-day) historical data for a day-based timeframe.
   *
   * Flow:
   *  1. Phase 1 — todayfill: fetch today's 1-min bars (only if not already in
   *     memory) so that the chart shows today's partial day bar even while the
   *     EOD historical load completes.
   *  2. Phase 2 — EOD backfill: `POST /backfill { candleInterval: "1D" }` to
   *     retrieve up to 20 years of daily bars, stored in `eodData[symbol]`.
   *
   * Does NOT reset `oneMinuteData` — live ticks and todayfill data continue
   * to accumulate so the current day's in-progress bar stays up-to-date.
   */
  const triggerEodFetch = (sym: typeof panel.symbol) => {
    if (!sym?.symbol) return;

    // Cancel any in-progress 1-min or previous EOD fetch
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Disable paginated 1-min backfill — EOD is a single-call fetch
    backfillPageRef.current = 1;
    backfillHasMoreRef.current = false;
    backfillLoadingRef.current = false;
    if (backfillDebounceRef.current) {
      clearTimeout(backfillDebounceRef.current);
      backfillDebounceRef.current = null;
    }

    // Reset scroll tracking so the chart auto-follows latest data
    isUserScrolledAwayRef.current = false;
    hasInitialFitRef.current = false;
    // Gate chart rendering while EOD history loads
    isFetchingRef.current = true;
    dispatch(gateHistoryLoading({ symbol: sym.symbol }));

    dispatch(clearPanelError(panelId));

    const scripId = sym.id;
    const symbolName = sym.symbol;

    if (!scripId) {
      dispatch(setPanelError({ panelId, error: `Symbol ID not found for ${symbolName} — cannot fetch history` }));
      return;
    }

    (async () => {
      // Phase 1: todayfill — skip when the 1-min data is already in memory
      // (e.g. user switches from 5m → 1D on a symbol already loaded)
      if (oneMinuteData.length === 0) {
        try {
          const todayBars = await chartBackfillHandler.fetchTodayData(scripId, symbolName);
          if (!controller.signal.aborted && todayBars.length > 0) {
            dispatch(prependHistoryData({ symbol: symbolName, bars: todayBars }));
          }
        } catch (error: any) {
          if (!controller.signal.aborted) {
            console.warn(`[Chart] EOD todayfill failed for ${symbolName}:`, error.message);
          }
        }
      }

      // Phase 2: EOD historical backfill
      let eodSuccess = false;
      try {
        const duration = loadEodDuration();
        console.log(`[Chart] Fetching EOD data for ${symbolName} (id=${scripId}, duration=${duration}d)`);
        const eodBars = await chartBackfillHandler.fetchEodHistory(scripId, symbolName, duration);
        if (controller.signal.aborted) return;
        if (eodBars.length > 0) {
          dispatch(prependEodData({ symbol: symbolName, bars: eodBars }));
          eodSuccess = true;
          console.log(`[Chart] EOD SUCCESS: ${eodBars.length} daily bars for ${symbolName}`);
        } else {
          console.warn(`[Chart] EOD returned 0 bars for ${symbolName} — falling back to 1M data`);
        }
      } catch (error: any) {
        if (!controller.signal.aborted) {
          console.error(`[Chart] EOD fetch FAILED for ${symbolName}:`, error?.message ?? error);
        }
      }

      // ── 1M fallback for 1D display ──────────────────────────────────────────────
      // When the EOD endpoint is unavailable, returns empty, or errors out,
      // load page 1 of 1-minute backfill (~30 days) so the 1D timeframe chart
      // can still show something via convertTimeframe(oneMinuteData, "1D").
      if (!eodSuccess && !controller.signal.aborted) {
        console.log(`[Chart] Loading 1M fallback data for 1D display (${symbolName})`);
        try {
          const { bars: fallbackBars } = await chartBackfillHandler.fetchHistoryData(scripId, symbolName);
          if (controller.signal.aborted) return;
          if (fallbackBars.length > 0) {
            dispatch(prependHistoryData({ symbol: symbolName, bars: fallbackBars }));
            console.log(`[Chart] 1M fallback: ${fallbackBars.length} bars loaded for ${symbolName}`);
          }
        } catch (error: any) {
          if (!controller.signal.aborted) {
            console.warn(`[Chart] 1M fallback also failed for ${symbolName}:`, error?.message ?? error);
          }
        }
      }

      // ── EOD history loaded — ungate live ticks ────────────────────
      if (!controller.signal.aborted) {
        dispatch(markHistoryLoaded({ symbol: symbolName }));
        isFetchingRef.current = false;
      }
    })();
  };

  // Get crosshair sync state from Redux
  const crosshairSyncEnabled = useSelector((state: any) => state.chart?.crosshairSyncEnabled ?? true);
  const crosshairTime = useSelector((state: any) => state.chart?.crosshairTime ?? null);
  // Track whether this panel originated the crosshair move (to avoid echo)
  const isCrosshairSourceRef = useRef(false);

  // Load persisted chart settings
  const chartSettings = useMemo<SavedChartSettings>(() => loadChartSettings(), []);

  // Get current chart type from panel state
  const currentChartType: ChartType = panel?.chartType || "candlestick";
  // Ref that always holds the latest chart type — used inside setInterval callbacks
  // to avoid stale closures when strategy indicators are loaded via template at mount.
  const currentChartTypeRef = useRef<ChartType>(currentChartType);
  currentChartTypeRef.current = currentChartType;

  // Initialize chart — autoSize fills the container automatically (v5)
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const settings = chartSettings;
    const gridVisible = settings.gridVisible;

    // Use per-pane background if set, otherwise global preset
    const bgPreset = loadPaneBgPreset(panelId) ?? loadChartBgPreset();

    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { color: bgPreset.bg },
        textColor: bgPreset.textColor,
      },
      grid: {
        vertLines: { color: gridVisible ? bgPreset.gridColor : "transparent" },
        horzLines: { color: gridVisible ? bgPreset.gridColor : "transparent" },
      },
      crosshair: {
        mode: settings.crosshairMode,
      },
      rightPriceScale: {
        borderColor: bgPreset.borderColor,
        visible: settings.priceScalePosition === "right",
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      leftPriceScale: {
        borderColor: bgPreset.borderColor,
        visible: settings.priceScalePosition === "left",
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor: bgPreset.borderColor,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5,
        // Prevent the visible time range from shifting when the chart
        // container is resized (e.g. when indicator panes are added/removed
        // and the main chart's vertical space is redistributed).
        lockVisibleTimeRangeOnResize: true,
      },
    });

    // Create series based on chart type
    let series: any;
    const upColor = settings.upColor;
    const downColor = settings.downColor;

    switch (currentChartType) {
      case "line":
        series = chart.addSeries(LineSeries, {
          color: upColor,
          lineWidth: 2,
          priceScaleId: settings.priceScalePosition,
        });
        break;
      case "area":
        series = chart.addSeries(AreaSeries, {
          lineColor: upColor,
          topColor: colorTheme === "dark" ? `${upColor}40` : `${upColor}30`,
          bottomColor: colorTheme === "dark" ? `${upColor}08` : `${upColor}05`,
          lineWidth: 2,
          priceScaleId: settings.priceScalePosition,
        });
        break;
      case "baseline":
        series = chart.addSeries(BaselineSeries, {
          baseValue: { type: "price", price: 0 },
          topLineColor: upColor,
          bottomLineColor: downColor,
          topFillColor1: `${upColor}28`,
          topFillColor2: `${upColor}05`,
          bottomFillColor1: `${downColor}05`,
          bottomFillColor2: `${downColor}28`,
          lineWidth: 2,
          priceScaleId: settings.priceScalePosition,
        });
        break;
      case "bar":
        series = chart.addSeries(BarSeries, {
          upColor: upColor,
          downColor: downColor,
          priceScaleId: settings.priceScalePosition,
        });
        break;
      case "candlestick":
      default:
        series = chart.addSeries(CandlestickSeries, {
          upColor: upColor,
          downColor: downColor,
          borderUpColor: upColor,
          borderDownColor: downColor,
          wickUpColor: upColor,
          wickDownColor: downColor,
          priceScaleId: settings.priceScalePosition,
        });
        break;
    }

    chartRef.current = chart;
    candleSeriesRef.current = series;
    lastCandleCountRef.current = 0;
    lastCandlesRef.current = [];

    // Attach indicator renderer to chart (with candle series for markers)
    indicatorRendererRef.current.attach(chart, series);

    // Subscribe to crosshair move for OHLC legend + crosshair sync
    // Throttle crosshair sync dispatch to avoid flooding Redux (fires on every mouse pixel)
    let lastCrosshairDispatchTime = 0;
    let pendingCrosshairRAF: number | null = null;

    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || !param.seriesData) {
        // Crosshair left the chart — show latest candle data
        setLegendData(null);
        // Dispatch null to clear sync crosshair from other panels
        if (isCrosshairSourceRef.current) {
          isCrosshairSourceRef.current = false;
          if (pendingCrosshairRAF) cancelAnimationFrame(pendingCrosshairRAF);
          dispatch(setCrosshairTime(null));
        }
        return;
      }

      // Dispatch crosshair time for sync across panels
      const timeVal = typeof param.time === 'object'
        ? new Date((param.time as any).year, (param.time as any).month - 1, (param.time as any).day).getTime() / 1000
        : (param.time as number);

      isCrosshairSourceRef.current = true;

      // Throttle: dispatch at most once per animation frame (~16ms)
      const now = performance.now();
      if (now - lastCrosshairDispatchTime > 32) {
        lastCrosshairDispatchTime = now;
        dispatch(setCrosshairTime(timeVal));
      } else if (!pendingCrosshairRAF) {
        pendingCrosshairRAF = requestAnimationFrame(() => {
          pendingCrosshairRAF = null;
          lastCrosshairDispatchTime = performance.now();
          dispatch(setCrosshairTime(timeVal));
        });
      }

      const data = param.seriesData.get(series) as any;
      if (data && (data.open !== undefined || data.value !== undefined)) {
        // For line/area charts, seriesData has { value } instead of OHLC
        const matchedCandle = lastCandlesRef.current.find(c => c.time === timeVal);

        if (data.open !== undefined) {
          // Candlestick / Bar
          setLegendData({
            time: timeVal,
            open: data.open,
            high: data.high,
            low: data.low,
            close: data.close,
            volume: matchedCandle?.volume ?? 0,
            oi: (matchedCandle as any)?.oi ?? 0,
            change: data.close - data.open,
          });
        } else if (data.value !== undefined) {
          // Line / Area — show close as all OHLC fields
          setLegendData({
            time: timeVal,
            open: matchedCandle?.open ?? data.value,
            high: matchedCandle?.high ?? data.value,
            low: matchedCandle?.low ?? data.value,
            close: data.value,
            volume: matchedCandle?.volume ?? 0,
            oi: (matchedCandle as any)?.oi ?? 0,
            change: matchedCandle ? (matchedCandle.close - matchedCandle.open) : 0,
          });
        }
      }
    });

    // Track user scroll state: when the visible range changes,
    // detect whether the latest bar is still in view.
    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range || !candleSeriesRef.current) return;
      // Skip entirely during programmatic chart modifications (indicator
      // add/remove, setData, layoutSeparatePanes).  Without this guard,
      // an auto-fit triggered by series.setData() corrupts the scroll
      // refs and causes the chart to drift to the left permanently.
      if (isProgrammaticScrollRef.current) return;
      const dataLen = lastCandlesRef.current.length;
      if (dataLen === 0) return;
      // If the right edge of the visible range is within a few bars of
      // the latest data point, the user is "at the latest"
      isUserScrolledAwayRef.current = range.to < dataLen - 3;
      // When the user is at the latest candle and this range change is
      // user-driven (not from our own scrollToPosition call), remember
      // their preferred right-side offset so live ticks don't override it.
      // NOTE: we only update the ref here — NO applyOptions() call.
      // Calling applyOptions({ rightOffset }) inside a subscribeVisibleLogicalRangeChange
      // callback triggers another range-change event, creating an infinite loop
      // that freezes the UI on every scroll/zoom.  The live-update path in the
      // main render effect already calls scrollToPosition(userRightOffsetRef.current)
      // on each tick, so the ref value is applied without any API call here.
      if (!isUserScrolledAwayRef.current && !isProgrammaticScrollRef.current) {
        const offset = range.to - (dataLen - 1);
        // Stability filter: only update the saved offset when the change
        // exceeds 0.3 bars.  series.update() on separate-pane indicators
        // (like Aroon) causes LWC to emit range-change events with tiny
        // floating-point fluctuations (<0.1 bar).  Without this filter,
        // these accumulate and slowly drift the chart position.
        const diff = Math.abs(offset - userRightOffsetRef.current);
        if (offset >= 1 && offset <= 50 && diff > 0.5) {
          userRightOffsetRef.current = offset;
        }
      }
    });

    // Bump version so the data-render effect re-applies candles to the new chart
    setChartVersion(v => v + 1);

    return () => {
      // Cancel any pending crosshair rAF to prevent stale dispatches after unmount
      if (pendingCrosshairRAF) cancelAnimationFrame(pendingCrosshairRAF);
      // Cancel any pending chart-positioning rAF
      if (positionRAFRef.current != null) {
        cancelAnimationFrame(positionRAFRef.current);
        positionRAFRef.current = null;
      }
      indicatorRendererRef.current?.detach();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
    };
  }, [colorTheme, currentChartType]);

  // ─── Crosshair Sync: receive crosshair time from other panels ──
  useEffect(() => {
    if (!chartRef.current || !crosshairSyncEnabled || crosshairTime === null) return;
    // Don't apply sync to the panel that originated the move
    if (isCrosshairSourceRef.current) {
      isCrosshairSourceRef.current = false;
      return;
    }
    try {
      (chartRef.current as any).setCrosshairPosition(
        undefined,
        candleSeriesRef.current,
        crosshairTime
      );
    } catch {
      // Ignore if time is out of range for this panel
    }
  }, [crosshairTime, crosshairSyncEnabled]);

  // Clear synced crosshair when sync time is null
  useEffect(() => {
    if (!chartRef.current || crosshairTime !== null) return;
    try {
      chartRef.current.clearCrosshairPosition();
    } catch {}
  }, [crosshairTime]);

  // ─── Alert Price Lines on Chart ────────────────────────────────
  const symbolAlerts = useSelector((state: any) => {
    const alerts: PriceAlertType[] = state.alerts?.alerts || [];
    return alerts.filter(a => a.symbol === panelSymbolName && !a.triggered);
  }, shallowEqual);
  const alertPriceLinesRef = useRef<any[]>([]);

  useEffect(() => {
    if (!candleSeriesRef.current) return;

    // Remove old price lines
    for (const pl of alertPriceLinesRef.current) {
      try { candleSeriesRef.current.removePriceLine(pl); } catch {}
    }
    alertPriceLinesRef.current = [];

    // Add new price lines for each active alert
    for (const alert of symbolAlerts) {
      try {
        const priceLine = candleSeriesRef.current.createPriceLine({
          price: alert.targetPrice,
          color: alert.condition.includes("above") ? "#26a69a" : "#ef5350",
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `Alert: ${alert.condition.replace("_", " ")}`,
        });
        alertPriceLinesRef.current.push(priceLine);
      } catch {}
    }

    return () => {
      for (const pl of alertPriceLinesRef.current) {
        try { candleSeriesRef.current?.removePriceLine(pl); } catch {}
      }
      alertPriceLinesRef.current = [];
    };
  }, [symbolAlerts, chartVersion]);

  // When the symbol is cleared (last panel closed), wipe the chart series so
  // old candlestick data doesn't remain visible behind the "Select a symbol" overlay.
  useEffect(() => {
    if (!panelSymbolName) {
      if (candleSeriesRef.current) {
        try { candleSeriesRef.current.setData([]); } catch (_) {}
      }
    }
  }, [panelSymbolName]); // eslint-disable-line

  // Fetch history data in background when symbol changes, timeframe switches to
  // intraday, or data is cleared (e.g. after re-login).
  useEffect(() => {
    if (panel?.symbol?.symbol) {
      // Day-based timeframes (1D, 1W, 1Mo, …) use EOD data — handled by the effect below
      if (isDayBasedTimeframe(panel.timeframe)) return;
      // Skip fetch if backfill was already successfully triggered for this
      // symbol AND we have data. Using a ref (not just oneMinuteData.length)
      // prevents falsely skipping when only a handful of live-tick bars exist
      // (e.g. user opens chart in 1D mode, a few live ticks arrive, then they
      // switch to 1M — without this guard the `length > 0` check would block
      // the very first backfill fetch).
      if (
        backfillFetchedForSymbolRef.current === panel.symbol.symbol &&
        oneMinuteData.length > 0
      ) {
        return;
      }
      triggerFetch(panel.symbol);
    }
  // panel?.timeframe is included so that switching from a day-based TF (where
  // fetch is skipped) to an intraday TF correctly fires a fresh backfill.
  // oneMinuteData.length is included so that re-login (clearing data) triggers
  // a fresh fetch without waiting for a symbol or timeframe change.
  }, [panel?.symbol?.symbol, panel?.timeframe, oneMinuteData.length]); // eslint-disable-line

  // For day-based timeframes: fetch EOD historical data when needed.
  // Triggers on: symbol change, timeframe switch into day-based,
  // eodVersion bump (duration setting changed), or logout clearing data.
  useEffect(() => {
    if (!panel?.symbol?.symbol) return;
    if (!isDayBasedTimeframe(panel?.timeframe ?? "1")) return;
    if (eodData.length > 0) return;
    triggerEodFetch(panel.symbol);
  }, [panel?.symbol?.symbol, panel?.timeframe, eodData.length, eodVersion]); // eslint-disable-line

  // When the EOD duration setting changes, clear cached EOD data so
  // the effect above re-fetches with the updated duration.
  useEffect(() => {
    const handleEodDurationChanged = () => {
      dispatch(clearAllEodData());
    };
    window.addEventListener("eod-duration-changed", handleEodDurationChanged);
    return () => window.removeEventListener("eod-duration-changed", handleEodDurationChanged);
  }, [dispatch]);

  // When the chart background preset changes, update layout/grid colors live.
  // Skip if this pane has a per-pane override.
  useEffect(() => {
    const handleBgChanged = () => {
      if (!chartRef.current) return;
      // If pane has its own bg, don't follow global change
      if (loadPaneBgPreset(panelId)) return;
      const preset = loadChartBgPreset();
      const settings = loadChartSettings();
      const gridVisible = settings.gridVisible;
      chartRef.current.applyOptions({
        layout: {
          background: { color: preset.bg },
          textColor: preset.textColor,
        },
        grid: {
          vertLines: { color: gridVisible ? preset.gridColor : "transparent" },
          horzLines: { color: gridVisible ? preset.gridColor : "transparent" },
        },
        rightPriceScale: { borderColor: preset.borderColor },
        leftPriceScale:  { borderColor: preset.borderColor },
        timeScale:       { borderColor: preset.borderColor },
      });
    };
    window.addEventListener("chart-bg-changed", handleBgChanged);
    return () => window.removeEventListener("chart-bg-changed", handleBgChanged);
  }, [panelId]); // panelId is stable

  // Apply saved chart style settings (candle up/down colors) without reloading the page.
  useEffect(() => {
    const applySeriesColors = (settings: SavedChartSettings) => {
      if (!candleSeriesRef.current) return;

      const upColor = settings.upColor;
      const downColor = settings.downColor;
      const type = currentChartTypeRef.current;

      try {
        switch (type) {
          case "line":
            candleSeriesRef.current.applyOptions({ color: upColor });
            break;
          case "area":
            candleSeriesRef.current.applyOptions({
              lineColor: upColor,
              topColor: colorTheme === "dark" ? withAlpha(upColor, "40") : withAlpha(upColor, "30"),
              bottomColor: colorTheme === "dark" ? withAlpha(upColor, "08") : withAlpha(upColor, "05"),
            });
            break;
          case "baseline":
            candleSeriesRef.current.applyOptions({
              topLineColor: upColor,
              bottomLineColor: downColor,
              topFillColor1: withAlpha(upColor, "28"),
              topFillColor2: withAlpha(upColor, "05"),
              bottomFillColor1: withAlpha(downColor, "05"),
              bottomFillColor2: withAlpha(downColor, "28"),
            });
            break;
          case "bar":
            candleSeriesRef.current.applyOptions({
              upColor,
              downColor,
            });
            break;
          case "candlestick":
          default:
            candleSeriesRef.current.applyOptions({
              upColor,
              downColor,
              borderUpColor: upColor,
              borderDownColor: downColor,
              wickUpColor: upColor,
              wickDownColor: downColor,
            });
            break;
        }
      } catch (err) {
        console.warn("[Chart] Failed to apply chart style settings:", err);
      }
    };

    const handleChartSettingsChanged = (event: Event) => {
      const customEvent = event as CustomEvent<SavedChartSettings | undefined>;
      const settings = customEvent.detail ?? loadChartSettings();
      applySeriesColors(settings);
    };

    window.addEventListener("chart-settings-changed", handleChartSettingsChanged as EventListener);
    return () => {
      window.removeEventListener("chart-settings-changed", handleChartSettingsChanged as EventListener);
    };
  }, [colorTheme]);

  // ─── Component Unmount Cleanup ──────────────────────────────────────
  // Cancel in-flight fetches, pending indicator param timers, and replay data
  useEffect(() => {
    return () => {
      // Cancel any in-flight todayfill/backfill HTTP requests
      abortRef.current?.abort();
      abortRef.current = null;

      // Reset backfill tracking so a remount triggers a fresh fetch
      backfillFetchedForSymbolRef.current = "";

      // Clear paginated backfill debounce timer
      if (backfillDebounceRef.current) {
        clearTimeout(backfillDebounceRef.current);
        backfillDebounceRef.current = null;
      }

      // Clear all pending indicator parameter debounce timers
      pendingParamsRef.current.forEach(({ timer }) => clearTimeout(timer));
      pendingParamsRef.current.clear();

      // Clear replay data to free memory
      replayAllCandlesRef.current = [];

      // Clear indicator update debounce timer
      if (indicatorUpdateTimerRef.current) {
        clearTimeout(indicatorUpdateTimerRef.current);
        indicatorUpdateTimerRef.current = null;
      }

      // Clear replay interval timer
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }

      // Release candle reference
      lastCandlesRef.current = [];

      // Cleanup compare series and abort controllers
      for (const [, controller] of compareAbortControllersRef.current) {
        controller.abort();
      }
      compareAbortControllersRef.current.clear();
    };
  }, []);

  // ─── Memoize converted candles — SINGLE conversion, used by chart render + indicators ──
  //
  // The cap is derived from the user's backfill preference so all fetched history
  // is available on the chart.  500 bars/calendar-day is a generous upper bound
  // that covers equity (375 min) and commodity (up to 855 min) instruments.
  // On desktop (Tauri) converting up to ~180 K bars takes <10 ms per tick, which
  // is well within the 100 ms tick-batch window.
  //
  // The full store stays in Redux for future scroll-based lazy loading.
  const _backfillDays = loadBackfillDays();
  const _intradayCap = Math.max(_backfillDays * 500, 10000);
  const MAX_CONVERT_BARS: Record<string, number> = {
    "1":  _intradayCap,
    "2":  _intradayCap,
    "3":  _intradayCap,
    "5":  _intradayCap,
    "7":  _intradayCap,
    "10": _intradayCap,
    "12": _intradayCap,
    "15": _intradayCap,
    "20": _intradayCap,
    "30": _intradayCap,
  };

  const convertedCandles = useMemo(() => {
    if (!panel || !panel.symbol) return EMPTY_CANDLES;

    if (isDayBasedTimeframe(panel.timeframe)) {
      // ── Primary: EOD path (up to 20 years of daily bars) ──
      if (eodData.length > 0) {
        // Derive today's partial day bar from 1-minute data (todayfill + live ticks),
        // then merge it with the historical EOD bars so the chart shows a live
        // current-day candle even while the market is open.
        let allDayBars: DayBar[] = [...eodData];
        if (oneMinuteData.length > 0) {
          const todayCandles = chartDataHandler.convertTimeframe(oneMinuteData, "1D");
          if (todayCandles.length > 0) {
            const last = todayCandles[todayCandles.length - 1];
            const todayDayBar: DayBar = {
              timestamp: last.time * 1000, // last.time is already UTC-equivalent seconds
              open: last.open,
              high: last.high,
              low: last.low,
              close: last.close,
              volume: last.volume,
              oi: last.oi || 0,
            };
            // Remove any EOD bar on the same day to avoid duplication
            allDayBars = allDayBars.filter(b => b.timestamp !== todayDayBar.timestamp);
            allDayBars.push(todayDayBar);
            allDayBars.sort((a, b) => a.timestamp - b.timestamp);
          }
        }
        return chartDataHandler.convertDayTimeframe(allDayBars, panel.timeframe);
      }

      // ── Fallback for 1D: derive daily bars from 1M data ──────────────────
      // Used when EOD data is unavailable (server error, offline, not configured).
      // Shows recent ~30-90 days of daily candles aggregated from 1-minute bars.
      // Chart updates automatically to EOD data when it loads in background.
      if (panel.timeframe === "1D" && oneMinuteData.length > 0) {
        return chartDataHandler.convertTimeframe(oneMinuteData, "1D");
      }

      return EMPTY_CANDLES;
    }

    // ── 1-minute path (intraday timeframes: 1m … 30m) ──
    if (oneMinuteData.length === 0) return EMPTY_CANDLES;
    // Cap input to avoid O(n) conversion over 150K bars on every tick.
    const cap = MAX_CONVERT_BARS[panel.timeframe] ?? 5000;
    const sliced = oneMinuteData.length > cap
      ? oneMinuteData.slice(oneMinuteData.length - cap)
      : oneMinuteData;
    return chartDataHandler.convertTimeframe(sliced, panel.timeframe);
  }, [oneMinuteData, eodData, panel?.timeframe]);

  // ─── Memoize IST-shifted full candles to avoid re-mapping every render ──
  const fullCandles = useMemo(() => {
    if (convertedCandles.length === 0) return EMPTY_CANDLES;
    return convertedCandles.map(candle => ({
      ...candle,
      time: (candle.time + IST_OFFSET) as UTCTimestamp,
    }));
  }, [convertedCandles]);

  const inferredPriceTick = useMemo(() => {
    if (convertedCandles.length === 0) return 0.05;
    return inferTickSize(convertedCandles as CandleData[]);
  }, [convertedCandles]);

  // Update chart data when timeframe or 1-min data changes
  useEffect(() => {
    if (!candleSeriesRef.current || !panel || !panel.symbol) {
      return;
    }

    // During bar-replay mode, the replay effect manages the series data.
    // Skip normal updates so live ticks don't overwrite the sliced replay.
    if (isReplayMode) return;

    // Skip rendering while a fetch is in progress.  Data is accumulating
    // in Redux (todayfill → backfill → markHistoryLoaded).  Without this
    // gate, each intermediate prependHistoryData dispatch triggers a full
    // setData() on the chart — multiple heavy synchronous cycles that
    // freeze the Tauri webview.  Once the fetch completes and sets
    // isFetchingRef to false, fullCandles updates and this effect fires
    // once with the complete dataset.
    if (isFetchingRef.current) return;

    if (fullCandles.length === 0) {
      // Symbol changed — clear old data so the price scale resets immediately
      try {
        candleSeriesRef.current.setData([]);
        lastCandlesRef.current = [];
        lastCandleCountRef.current = 0;
      } catch {}
      return;
    }

    try {
      // Format data for the current chart type
      const isValueFormat = currentChartType === "line" || currentChartType === "area" || currentChartType === "baseline";
      const seriesData: any[] = isValueFormat
        ? fullCandles.map(c => ({ time: c.time, value: c.close }))
        : fullCandles;

      // Detect timeframe switch — always do full setData when timeframe changes
      const timeframeChanged = panel.timeframe !== lastRenderedTimeframeRef.current;
      lastRenderedTimeframeRef.current = panel.timeframe;
      if (timeframeChanged) {
        hasInitialFitRef.current = false;
        // Timeframe switch = fresh view — auto-follow the latest candle
        isUserScrolledAwayRef.current = false;
        // Reset Y-axis auto-scale for the new timeframe so the price range
        // fits the new data correctly (user's manual Y adjustment from the
        // previous timeframe should not carry over).
        const scaleId = chartSettings.priceScalePosition || "right";
        try { chartRef.current?.priceScale(scaleId).applyOptions({ autoScale: true }); } catch (_) {}
      }

      // When an indicator apply function bumps colorMapVersion, we need
      // a full setData (not incremental update) so ALL candles get recolored.
      const colorRedrawNeeded = needsColorRedrawRef.current;
      needsColorRedrawRef.current = false;

      const prev = lastCandlesRef.current;
      const isLiveUpdate =
        !timeframeChanged &&
        !colorRedrawNeeded &&
        prev.length > 0 &&
        fullCandles.length >= prev.length &&
        fullCandles[0].time === prev[0].time;

      if (isLiveUpdate && fullCandles.length === prev.length) {
        // Same number of candles - last bar updated in place
        const lastCandle = seriesData[seriesData.length - 1];
        // Guard: skip update if candle has null/invalid values to prevent
        // lightweight-charts from throwing "Value is null".
        const lastCandleValid = isValueFormat
          ? lastCandle.value != null && isFinite(lastCandle.value)
          : lastCandle.open != null && lastCandle.high != null && lastCandle.low != null && lastCandle.close != null &&
            isFinite(lastCandle.open) && isFinite(lastCandle.high) && isFinite(lastCandle.low) && isFinite(lastCandle.close);
        if (!lastCandleValid) return;
        // Preserve strategy candle colors during live tick updates
        const colorMapActive = candleColorMapRef.current;
        if (colorMapActive.size > 0 && lastCandle.time != null) {
          const col = colorMapActive.get(lastCandle.time as number);
          if (col) {
            lastCandle.color = col;
            lastCandle.borderColor = col;
            lastCandle.wickColor = col;
          }
        }
        candleSeriesRef.current.update(lastCandle);

        // Keep the chart following the latest price when the user
        // hasn't deliberately scrolled away
        if (!isUserScrolledAwayRef.current && chartRef.current) {
          // DO NOT call priceScale.applyOptions({ autoScale }) here.
          // - When autoScale is already true, it triggers a redundant fullUpdate
          //   which drifts the chart when separate-pane indicators exist.
          // - When autoScale is false (user manually adjusted Y-axis), we must
          //   respect their choice. AutoScale is re-enabled explicitly by
          //   handleScrollToLatest, symbol change, or timeframe change.
          //
          // Guard: scrollToPosition fires range-change events asynchronously
          // (in LWC's next rAF).  With separate-pane indicators, LWC's
          // _correctOffset() can cascade across 2+ frames, producing transient
          // offsets that differ from what we requested.  Without locking the
          // guard, the range-change handler captures these transient values
          // and slowly drifts userRightOffsetRef.
          isProgrammaticScrollRef.current = true;
          chartRef.current.timeScale().scrollToPosition(userRightOffsetRef.current, false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isProgrammaticScrollRef.current = false;
            });
          });
        }
      } else if (isLiveUpdate && fullCandles.length <= prev.length + 2) {
        // One or two new candles added — use incremental update to avoid resetting the view
        const newCount = fullCandles.length - prev.length;
        const colorMapActive = candleColorMapRef.current;
        for (let i = Math.max(0, seriesData.length - newCount - 1); i < seriesData.length; i++) {
          const candleToUpdate = seriesData[i];
          // Guard: skip candles with null/invalid values
          const candleValid = isValueFormat
            ? candleToUpdate.value != null && isFinite(candleToUpdate.value)
            : candleToUpdate.open != null && candleToUpdate.high != null && candleToUpdate.low != null && candleToUpdate.close != null &&
              isFinite(candleToUpdate.open) && isFinite(candleToUpdate.high) && isFinite(candleToUpdate.low) && isFinite(candleToUpdate.close);
          if (!candleValid) continue;
          if (colorMapActive.size > 0 && candleToUpdate.time != null) {
            const col = colorMapActive.get(candleToUpdate.time as number);
            if (col) {
              candleToUpdate.color = col;
              candleToUpdate.borderColor = col;
              candleToUpdate.wickColor = col;
            }
          }
          candleSeriesRef.current.update(candleToUpdate);
        }

        // Auto-follow latest candle when a new bar appears
        if (!isUserScrolledAwayRef.current && chartRef.current) {
          // DO NOT call priceScale.applyOptions({ autoScale }) here.
          // Same rationale as above — preserve user Y-axis adjustments.
          isProgrammaticScrollRef.current = true;
          chartRef.current.timeScale().scrollToPosition(userRightOffsetRef.current, false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isProgrammaticScrollRef.current = false;
            });
          });
        }
      } else {
        // Full setData path — symbol change, timeframe change, backfill arrival, or merge

        // Save the user's zoom level (number of visible bars) and their
        // offset from the right edge BEFORE setData resets the scroll.
        // We store how many bars from the right edge the view ends at,
        // so after prepended bars shift indices, we can restore correctly.
        let savedRange: { from: number; to: number } | null = null;
        if (hasInitialFitRef.current && chartRef.current) {
          try {
            const lr = chartRef.current.timeScale().getVisibleLogicalRange();
            if (lr) {
              const visibleBars = lr.to - lr.from;
              // Anchor to the right edge: keep the same distance from the end
              const rightEdgeOffset = prev.length - lr.to;
              const newTo = fullCandles.length - rightEdgeOffset;
              savedRange = {
                from: newTo - visibleBars,
                to: newTo,
              };
            }
          } catch {}
        }

        // Apply strategy candle coloring BEFORE setData so we only call
        // setData once (calling it twice with 5K+ bars freezes the UI).
        let finalData = seriesData;
        if (candleColorMapRef.current.size > 0 && !isValueFormat) {
          const colorMap = candleColorMapRef.current;
          finalData = seriesData.map((c: any) => {
            const col = colorMap.get(c.time as number);
            if (col) {
              return { ...c, color: col, borderColor: col, wickColor: col };
            }
            return c;
          });
        }
        // Filter out candles with null/NaN OHLC values before passing to
        // lightweight-charts — it throws "Value is null" for any null field.
        const validFinalData = isValueFormat
          ? finalData.filter((c: any) => c.value != null && isFinite(c.value))
          : finalData.filter((c: any) =>
              c.open != null && c.high != null && c.low != null && c.close != null &&
              isFinite(c.open) && isFinite(c.high) && isFinite(c.low) && isFinite(c.close));
        candleSeriesRef.current.setData(validFinalData);

        if (currentChartType === "baseline" && fullCandles.length > 0) {
          candleSeriesRef.current.applyOptions({
            baseValue: { type: "price" as const, price: fullCandles[0].close },
          });
        }

        // Minimum number of bar-widths to display so candles are never
        // stretched across the full screen when only a few bars exist.
        const MIN_VISIBLE_RANGE = 50;

        // Cancel any pending positioning frame from a previous data update
        // (e.g. todayfill arrived, RAF queued, then backfill arrives before
        // that frame fires — we want only the backfill positioning to apply).
        if (positionRAFRef.current != null) {
          cancelAnimationFrame(positionRAFRef.current);
          positionRAFRef.current = null;
        }

        // Preserve the user's current zoom whenever we already have one
        // (savedRange is non-null only when hasInitialFitRef is true and the
        // chart has a visible range).  This covers TWO scenarios:
        //   1. User scrolled away to view older data — keep their position.
        //   2. User manually zoomed in/out while still tracking the latest
        //      candle — keep their zoom level instead of snapping back to
        //      the default 150-bar view on every live-tick color refresh
        //      (strategy indicators trigger setData via colorRedrawNeeded).
        // The default 150-bar fit only applies on initial load / symbol
        // change / timeframe change — when savedRange is null.
        if (savedRange) {
          // User deliberately scrolled to view older data — preserve their
          // zoom level and scroll position so backfill/merge doesn't jump.
          const rangeWidth = savedRange.to - savedRange.from;
          if (rangeWidth < MIN_VISIBLE_RANGE) {
            savedRange = {
              from: savedRange.to - MIN_VISIBLE_RANGE,
              to: savedRange.to,
            };
          }
          const rangeCopy = { ...savedRange };
          positionRAFRef.current = requestAnimationFrame(() => {
            positionRAFRef.current = null;
            try {
              chartRef.current?.timeScale().setVisibleLogicalRange(rangeCopy);
            } catch {}
          });
        } else {
          // ── MT4-style chart-shift: latest candle always anchored near ──
          // ── the right edge with a small gap (rightOffset bar widths). ──
          //
          // scrollToPosition / setVisibleLogicalRange called synchronously
          // right after setData() often have no effect because the chart
          // hasn't processed the new data yet.  Deferring to the next
          // animation frame lets the chart finish internal layout first,
          // guaranteeing the positioning APIs work on the actual data.
          const dataLen = fullCandles.length;
          const rightOffset = userRightOffsetRef.current;

          positionRAFRef.current = requestAnimationFrame(() => {
            positionRAFRef.current = null;
            const chart = chartRef.current;
            if (!chart) return;
            const ts = chart.timeScale();

            // Set zoom level: show last ~150 bars when we have enough data.
            // ONLY call setVisibleLogicalRange when dataLen >= barsToShow so
            // that `from` is never negative.  A negative `from` creates a
            // huge empty area that pushes candles off the visible viewport
            // (e.g. switching from 1D → 3M with few intraday bars).
            const DEFAULT_VISIBLE_BARS = templateVisibleBarsRef.current ?? (isMobile ? 60 : 150);
            const barsToShow = Math.max(MIN_VISIBLE_RANGE, Math.min(dataLen, DEFAULT_VISIBLE_BARS));
            if (dataLen >= barsToShow) {
              try {
                ts.setVisibleLogicalRange({
                  from: dataLen - barsToShow,
                  to: dataLen - 1 + rightOffset,
                });
              } catch {}
            }

            // Anchor the latest candle near the right edge.
            // scrollToPosition works regardless of data count: the latest bar
            // is placed exactly `rightOffset` bar-widths from the right border.
            // NOTE: Do NOT wrap in isProgrammaticScrollRef — the sync
            // true→false cycle breaks the indicator structural update guard.
            try {
              ts.scrollToPosition(rightOffset, false);
            } catch {}

            // Only mark initial fit as done once we have enough data for
            // a proper zoom.  This ensures backfill arrival re-applies zoom.
            if (dataLen >= MIN_VISIBLE_RANGE) {
              hasInitialFitRef.current = true;
            }
          });
        }
      }

      lastCandleCountRef.current = fullCandles.length;
      lastCandlesRef.current = fullCandles;
    } catch (error) {
      console.error("Error updating chart data:", error);
    }
  }, [fullCandles, panel?.timeframe, chartVersion, currentChartType, isReplayMode, colorMapVersion]);

  // When replay mode changes, update the chart data
  useEffect(() => {
    if (!isReplayMode || !chartRef.current || !candleSeriesRef.current) return;
    const sliced = replayAllCandlesRef.current.slice(0, replayBar);
    if (sliced.length === 0) return;

    try {
      const chartType = panel?.chartType || "candles";
      if (chartType === "candles" || chartType === "hollow") {
        candleSeriesRef.current.setData(sliced.map((c: CandleData) => ({
          time: c.time as UTCTimestamp,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        })));
      } else {
        candleSeriesRef.current.setData(sliced.map((c: CandleData) => ({
          time: c.time as UTCTimestamp,
          value: c.close,
        })));
      }
    } catch (e) {
      // ignore
    }
  }, [isReplayMode, replayBar]);

  // ─── Indicator Render Effect ──────────────────────────────────────
  // Uses debounced scheduling so rapid live ticks batch together.
  useEffect(() => {
    if (!chartRef.current) return;

    // When all indicators removed, clean up renderer immediately
    if (activeIndicators.length === 0) {
      if (indicatorRendererRef.current.getSeriesCount() > 0) {
        indicatorRendererRef.current.removeAll();
      }
      return;
    }

    if (convertedCandles.length === 0) return;

    // Clear any pending debounce
    if (indicatorUpdateTimerRef.current) {
      clearTimeout(indicatorUpdateTimerRef.current);
    }

    const forceFullRecalc = indicatorNeedsFullRecalcRef.current;
    indicatorNeedsFullRecalcRef.current = false;

    // Debounce: 50ms for live ticks, immediate for full recalcs
    const delay = forceFullRecalc ? 0 : 50;

    indicatorUpdateTimerRef.current = setTimeout(() => {
      try {
        // savedRange will be captured in onBeforeRender (called synchronously
        // inside the Promise.all handler, just before chart modifications).
        let savedRange: { from: number; to: number } | null = null;
        let savedScrolledAway = isUserScrolledAwayRef.current;
        let savedRightOffset = userRightOffsetRef.current;

        indicatorRendererRef.current.update(
          activeIndicators,
          convertedCandles,
          IST_OFFSET,
          forceFullRecalc,
          // onBeforeRender: called synchronously inside Promise.all handler
          // BEFORE any addSeries/setData/layoutSeparatePanes.  Sets the
          // programmatic-scroll guard so the range-change listener ignores
          // all range mutations caused by indicator chart modifications.
          () => {
            isProgrammaticScrollRef.current = true;
            try {
              savedRange = chartRef.current?.timeScale().getVisibleLogicalRange() as { from: number; to: number } | null;
            } catch (_) {}
            savedScrolledAway = isUserScrolledAwayRef.current;
            savedRightOffset = userRightOffsetRef.current;
          },
          // onAfterRender: called synchronously after STRUCTURAL chart
          // modifications (addSeries, setData, layout changes — NOT live ticks).
          // Uses triple-rAF to ensure we run AFTER LWC's multi-frame layout
          // cascade (fullUpdate → adjustSizeImpl → secondary layout → settle).
          // Separate-pane indicators (like Aroon) trigger scaleMargins changes
          // that cause 2-3 frames of layout cascading inside LWC.
          () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  try {
                    if (savedRange && chartRef.current) {
                      chartRef.current.timeScale().setVisibleLogicalRange(savedRange);
                    }
                  } catch (_) {}
                  try {
                    if (chartRef.current) {
                      chartRef.current.timeScale().scrollToPosition(savedRightOffset, false);
                    }
                  } catch (_) {}
                  // Restore the scroll state that was saved before modifications
                  isUserScrolledAwayRef.current = savedScrolledAway;
                  userRightOffsetRef.current = savedRightOffset;
                  isProgrammaticScrollRef.current = false;
                });
              });
            });
          },
          // onScrollGuard: called for LIVE TICK updates (series.update only)
          // to temporarily lock/unlock the scroll guard.  Even a simple
          // series.update() on a separate-pane indicator (like Aroon) causes
          // LWC to recalculate its time scale internally (_correctOffset),
          // which fires subscribeVisibleLogicalRangeChange with a transiently
          // shifted offset.  Without locking the guard, the range-change
          // handler captures that offset → next tick uses it → slow drift.
          //
          // IMPORTANT: The unlock MUST be deferred by at least TWO animation
          // frames.  LWC processes series.update() in its own rAF, but for
          // separate-pane indicators with custom scaleMargins, the layout
          // can cascade across 2+ frames (_correctOffset → fullUpdate →
          // adjustSizeImpl).  A single-rAF defer is insufficient — the
          // second cascade frame fires AFTER the guard is released,
          // allowing the range-change handler to capture the transient
          // offset and slowly drift the chart position.
          (locked: boolean) => {
            if (locked) {
              isProgrammaticScrollRef.current = true;
            } else {
              // Double-rAF defer: LWC's rAF-1 (queued by series.update)
              // fires first, then our rAF-1 fires, then LWC's potential
              // rAF-2 (secondary cascade), then our rAF-2 unlocks.  This
              // ensures the guard is held through all LWC layout frames.
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  isProgrammaticScrollRef.current = false;
                });
              });
            }
          },
        );
      } catch (error) {
        console.error("[Chart] Error updating indicators:", error);
      }
    }, delay);

    return () => {
      if (indicatorUpdateTimerRef.current) {
        clearTimeout(indicatorUpdateTimerRef.current);
      }
    };
  }, [convertedCandles, activeIndicators, chartVersion]);

  // ─── Alpha Trade Dashboard Detection ──────────────────────────────
  // When Alpha Trade is active, poll the renderer's metadata store to
  // get dashboard values (signal, entry, SL, targets, P/L, status).
  const alphaTradeActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "alpha_trade" && a.visible),
    [activeIndicators],
  );

  const alphaTradeInstance = useMemo(
    () => activeIndicators.find(a => a.indicatorId === "alpha_trade" && a.visible) || null,
    [activeIndicators],
  );

  const atAlertEntryEnabled = Boolean(alphaTradeInstance?.params?.alertEntry ?? true);
  const atAlertTP1Enabled = Boolean(alphaTradeInstance?.params?.alertTP1 ?? true);
  const atAlertTP2Enabled = Boolean(alphaTradeInstance?.params?.alertTP2 ?? true);
  const atAlertSLEnabled = Boolean(alphaTradeInstance?.params?.alertSL ?? true);

  const formatAtEventTime = useCallback((ts: number) => {
    const date = new Date((ts + IST_OFFSET) * 1000);
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    const hh = String(date.getUTCHours()).padStart(2, "0");
    const min = String(date.getUTCMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }, [IST_OFFSET]);

  const playAtAlertSound = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.45);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.45);
    } catch {
      // Sound unavailable on this platform/browser — ignore.
    }
  }, []);

  const sendAtOSNotification = useCallback(async (title: string, body: string) => {
    try {
      const { sendNotification, isPermissionGranted, requestPermission } =
        await import("@tauri-apps/plugin-notification");
      let permitted = await isPermissionGranted();
      if (!permitted) {
        const result = await requestPermission();
        permitted = result === "granted";
      }
      if (permitted) {
        sendNotification({ title, body });
        return;
      }
    } catch {
      // Tauri notification plugin unavailable — try browser API.
    }

    try {
      if ("Notification" in window) {
        if (Notification.permission === "granted") {
          new Notification(title, { body });
        } else if (Notification.permission !== "denied") {
          const perm = await Notification.requestPermission();
          if (perm === "granted") {
            new Notification(title, { body });
          }
        }
      }
    } catch {
      // Ignore when system notifications are not available.
    }
  }, []);

  const pushAtAlertNotification = useCallback((title: string, message: string, symbol: string) => {
    dispatch(addNotification({
      type: "alert",
      title,
      message,
      symbol,
    }));
    toast(`🔔 ${title}`, { duration: 6000 });
    void sendAtOSNotification(title, message);
    if (alertSoundEnabled) {
      playAtAlertSound();
    }
  }, [dispatch, sendAtOSNotification, alertSoundEnabled, playAtAlertSound]);

  const processAtAlertEvents = useCallback((meta: any) => {
    const symbol = panel?.symbol?.symbol;
    if (!symbol || !panel?.timeframe) return;

    const ctxKey = `${panelId}:${symbol}:${panel.timeframe}`;
    if (atAlertContextRef.current !== ctxKey) {
      atAlertContextRef.current = ctxKey;
      atAlertInitializedRef.current = false;
      atSeenEventKeysRef.current.clear();
      atAlertBaselineTimeRef.current = null;
    }

    const arrows: Array<{ time: number; direction: "long" | "short"; type?: string }> =
      Array.isArray(meta?.arrowMarkers) ? meta.arrowMarkers : [];

    if (atAlertBaselineTimeRef.current == null) {
      const candles = lastCandlesRef.current;
      const closedBarTime = candles.length >= 2 ? Number(candles[candles.length - 2]?.time ?? 0) : 0;
      const liveBarTime = candles.length >= 1 ? Number(candles[candles.length - 1]?.time ?? 0) : 0;
      atAlertBaselineTimeRef.current = Math.max(closedBarTime, liveBarTime, Math.floor(Date.now() / 1000));
    }

    if (!atAlertInitializedRef.current) {
      for (const a of arrows) {
        const t = a.type ?? "signal";
        atSeenEventKeysRef.current.add(`${ctxKey}:${t}:${a.direction}:${a.time}`);
      }
      atAlertInitializedRef.current = true;
      return;
    }

    const timeframeLabel = getTimeframeConfig(panel.timeframe).label;
    const dash = meta?.dashboard as AlphaTradeDashboard | undefined;
    const ordered = [...arrows].sort((a, b) => a.time - b.time);
    const fmt = (n: number | null) => n != null ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-";

    for (const a of ordered) {
      const eventType = (a.type ?? "signal").toLowerCase();
      const eventKey = `${ctxKey}:${eventType}:${a.direction}:${a.time}`;
      if (atSeenEventKeysRef.current.has(eventKey)) continue;

      atSeenEventKeysRef.current.add(eventKey);
      if (atSeenEventKeysRef.current.size > 400) {
        const first = atSeenEventKeysRef.current.values().next().value;
        if (first) atSeenEventKeysRef.current.delete(first);
      }

      if (a.time <= (atAlertBaselineTimeRef.current ?? 0)) continue;

      const isSignal = eventType === "signal";
      const isTP1 = eventType === "tp1";
      const isTP2 = eventType === "tp2";
      const isSL = eventType === "sl";
      if ((isSignal && !atAlertEntryEnabled) || (isTP1 && !atAlertTP1Enabled) || (isTP2 && !atAlertTP2Enabled) || (isSL && !atAlertSLEnabled)) {
        continue;
      }

      const crossPanelKey = `alpha_trade:${symbol}:${panel.timeframe}:${eventType}:${a.direction}:${a.time}`;
      if (!shouldEmitCrossPanelStrategyAlert(crossPanelKey)) {
        continue;
      }

      const ts = formatAtEventTime(a.time);
      const signalSide = a.direction === "long" ? "BUY" : "SELL";
      const entry = dash?.entry ?? null;
      const sl = dash?.stopLoss ?? null;
      const t1 = dash?.target1 ?? null;
      const t2 = dash?.target2 ?? null;

      if (isSignal) {
        const title = `ALPHA TRADE ${signalSide} Signal`;
        const message = `ALPHA TRADE | ${symbol} | ${timeframeLabel} | ${signalSide} @ ${fmt(entry)} | T1 ${fmt(t1)} | T2 ${fmt(t2)} | SL ${fmt(sl)} | Signal Time ${ts}`;
        pushAtAlertNotification(title, message, symbol);
        continue;
      }

      let bookedPoints = dash?.bookedPL ?? 0;
      if (entry != null) {
        if (isTP1 && t1 != null) {
          bookedPoints = a.direction === "long" ? (t1 - entry) : (entry - t1);
        } else if (isTP2 && t2 != null) {
          bookedPoints = a.direction === "long" ? (t2 - entry) : (entry - t2);
        } else if (isSL && sl != null) {
          bookedPoints = a.direction === "long" ? (sl - entry) : (entry - sl);
        }
      }

      const eventLabel = isTP1 ? "Target1" : isTP2 ? "Target2" : "Stop Loss";
      const pointsText = `${bookedPoints >= 0 ? "+" : ""}${bookedPoints.toFixed(2)} pts`;
      const title = `ALPHA TRADE ${eventLabel} Triggered`;
      const message = `ALPHA TRADE | ${symbol} | ${timeframeLabel} | ${eventLabel} Triggered | Booked P&L ${pointsText} | Triggered Time ${ts}`;
      pushAtAlertNotification(title, message, symbol);
    }
  }, [
    panel?.symbol?.symbol,
    panel?.timeframe,
    panelId,
    atAlertEntryEnabled,
    atAlertTP1Enabled,
    atAlertTP2Enabled,
    atAlertSLEnabled,
    formatAtEventTime,
    pushAtAlertNotification,
  ]);

  const applyAlphaTradeCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "alpha_trade";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!alphaTradeActive) {
      setAlphaTradeData(null);
      setAtArrowBadges([]);
      atAlertInitializedRef.current = false;
      atSeenEventKeysRef.current.clear();
      atAlertContextRef.current = "";
      atAlertBaselineTimeRef.current = null;
      if (candleColorOwnerRef.current === "alpha_trade") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("alpha_trade");
      if (meta?.dashboard) {
        setAlphaTradeData(meta.dashboard as AlphaTradeDashboard);
      }
      processAtAlertEvents(meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyAlphaTradeCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("alpha_trade");
    if (meta?.dashboard) {
      setAlphaTradeData(meta.dashboard as AlphaTradeDashboard);
    }
    processAtAlertEvents(meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyAlphaTradeCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [alphaTradeActive, processAtAlertEvents]);

  // ─── Alpha Trade Arrow Badges ────────────────────────────────
  const computeAtArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !alphaTradeActive) {
      setAtArrowBadges([]); return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("alpha_trade");
    const arrows: { time: number; direction: "long" | "short"; price: number; type?: string; position?: string }[] = meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setAtArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short"; type: string; position: string }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as any);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction, type: arrow.type ?? "signal", position: arrow.position ?? (arrow.direction === "long" ? "below" : "above") });
        }
      } catch (_) {}
    }
    setAtArrowBadges(badges);
  }, [alphaTradeActive]);

  useEffect(() => {
    if (!alphaTradeActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeAtArrowBadges);
    const poll = setInterval(computeAtArrowBadges, 500);
    computeAtArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeAtArrowBadges); } catch (_) {}
    };
  }, [alphaTradeActive, computeAtArrowBadges]);

  const resetStrategyAlertState = useCallback((indicatorId: string) => {
    strategyAlertContextRef.current.delete(indicatorId);
    strategyAlertInitializedRef.current.delete(indicatorId);
    strategyAlertSeenRef.current.delete(indicatorId);
    strategyAlertBaselineRef.current.delete(indicatorId);
    strategyAlertLastStatusRef.current.delete(indicatorId);
  }, []);

  const processStrategyAlertEvents = useCallback((indicatorId: string, meta: any) => {
    const instance = activeIndicators.find(a => a.indicatorId === indicatorId && a.visible);
    const symbol = panel?.symbol?.symbol;
    if (!instance || !symbol || !panel?.timeframe) return;

    const ctxKey = `${panelId}:${symbol}:${panel.timeframe}:${indicatorId}`;
    if (strategyAlertContextRef.current.get(indicatorId) !== ctxKey) {
      strategyAlertContextRef.current.set(indicatorId, ctxKey);
      strategyAlertInitializedRef.current.set(indicatorId, false);
      strategyAlertSeenRef.current.set(indicatorId, new Set());
      strategyAlertBaselineRef.current.delete(indicatorId);
      strategyAlertLastStatusRef.current.delete(indicatorId);
    }

    const seen = strategyAlertSeenRef.current.get(indicatorId) ?? new Set<string>();
    strategyAlertSeenRef.current.set(indicatorId, seen);

    const currentBaseline = strategyAlertBaselineRef.current.get(indicatorId);
    if (currentBaseline == null) {
      const candles = lastCandlesRef.current;
      const closedBarTime = candles.length >= 2 ? Number(candles[candles.length - 2]?.time ?? 0) : 0;
      const liveBarTime = candles.length >= 1 ? Number(candles[candles.length - 1]?.time ?? 0) : 0;
      strategyAlertBaselineRef.current.set(indicatorId, Math.max(closedBarTime, liveBarTime, Math.floor(Date.now() / 1000)));
    }

    const baseline = strategyAlertBaselineRef.current.get(indicatorId) ?? 0;
    const masterEnabled = Boolean(instance.params?.alertEnabled ?? true);
    const allowEntry = masterEnabled && Boolean(instance.params?.alertEntry ?? true);
    const allowTP1 = masterEnabled && Boolean(instance.params?.alertTP1 ?? true);
    const allowTP2 = masterEnabled && Boolean(instance.params?.alertTP2 ?? true);
    const allowSL = masterEnabled && Boolean(instance.params?.alertSL ?? true);

    const dashboard = meta?.dashboard as Record<string, any> | undefined;
    const arrows: Array<{ time: number; direction: "long" | "short"; type?: string }> =
      Array.isArray(meta?.arrowMarkers) ? meta.arrowMarkers : [];

    const normalizeEventType = (rawType?: string): "signal" | "tp1" | "tp2" | "tp3" | "sl" | "tsl" => {
      const t = (rawType || "signal").toLowerCase().replace(/[_\s-]+/g, "");
      if (t.includes("tp1") || t.includes("tgt1") || t.includes("target1")) return "tp1";
      if (t.includes("tp2") || t.includes("tgt2") || t.includes("target2")) return "tp2";
      if (t.includes("tp3") || t.includes("tgt3") || t.includes("target3")) return "tp3";
      if (t.includes("tsl") || t.includes("trail")) return "tsl";
      if (t === "sl" || t.includes("stop")) return "sl";
      if (t === "entry" || t === "signal") return "signal";
      return "signal";
    };

    const isAllowedForType = (eventType: "signal" | "tp1" | "tp2" | "tp3" | "sl" | "tsl") => {
      if (eventType === "signal") return allowEntry;
      if (eventType === "tp1") return allowTP1;
      if (eventType === "tp2" || eventType === "tp3") return allowTP2;
      return allowSL;
    };

    const firstNumber = (candidates: any[]): number | null => {
      for (const v of candidates) {
        if (typeof v === "number" && Number.isFinite(v)) return v;
      }
      return null;
    };

    const sideKey = (direction: "long" | "short", key: "entry" | "t1" | "t2" | "t3" | "sl" | "tsl") => {
      if (!dashboard) return null;
      if (direction === "long") {
        if (key === "entry") return firstNumber([dashboard.buyEntry, dashboard.entry]);
        if (key === "t1") return firstNumber([dashboard.buyTP1, dashboard.target1, dashboard.target]);
        if (key === "t2") return firstNumber([dashboard.buyTP2, dashboard.target2]);
        if (key === "t3") return firstNumber([dashboard.target3]);
        if (key === "sl") return firstNumber([dashboard.buySL, dashboard.stopLoss]);
        return firstNumber([dashboard.trailingStop, dashboard.trailingSL]);
      }
      if (key === "entry") return firstNumber([dashboard.sellEntry, dashboard.entry]);
      if (key === "t1") return firstNumber([dashboard.sellTP1, dashboard.target1, dashboard.target]);
      if (key === "t2") return firstNumber([dashboard.sellTP2, dashboard.target2]);
      if (key === "t3") return firstNumber([dashboard.target3]);
      if (key === "sl") return firstNumber([dashboard.sellSL, dashboard.stopLoss]);
      return firstNumber([dashboard.trailingStop, dashboard.trailingSL]);
    };

    const fmtPrice = (n: number | null) => n != null
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "-";

    const fmtPoints = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(2)} pts`;

    const buildStrategyContextTag = () => {
      const tags: string[] = [];
      if (typeof dashboard?.preset === "string" && dashboard.preset.trim()) tags.push(dashboard.preset);
      if (typeof dashboard?.formula === "string" && dashboard.formula.trim()) tags.push(dashboard.formula);
      if (typeof dashboard?.timeframeSub === "string" && dashboard.timeframeSub.trim()) tags.push(dashboard.timeframeSub);
      if (typeof dashboard?.scriptName === "string" && dashboard.scriptName.trim()) tags.push(dashboard.scriptName);
      if (tags.length === 0) return "";
      return ` | Context ${tags.join(" / ")}`;
    };

    const buildLevelText = (
      eventType: "signal" | "tp1" | "tp2" | "tp3" | "sl" | "tsl",
      direction: "long" | "short",
    ) => {
      const entry = sideKey(direction, "entry");
      const t1 = sideKey(direction, "t1");
      const t2 = sideKey(direction, "t2");
      const t3 = sideKey(direction, "t3");
      const sl = sideKey(direction, "sl");
      const tsl = sideKey(direction, "tsl");

      const levels: string[] = [];
      if (entry != null) levels.push(`Entry ${fmtPrice(entry)}`);
      if (t1 != null) levels.push(`T1 ${fmtPrice(t1)}`);
      if (t2 != null) levels.push(`T2 ${fmtPrice(t2)}`);
      if (t3 != null) levels.push(`T3 ${fmtPrice(t3)}`);
      if (sl != null) levels.push(`SL ${fmtPrice(sl)}`);
      if (eventType === "tsl" && tsl != null) levels.push(`TSL ${fmtPrice(tsl)}`);

      let triggerPrice: number | null = null;
      if (eventType === "tp1") triggerPrice = t1;
      else if (eventType === "tp2") triggerPrice = t2;
      else if (eventType === "tp3") triggerPrice = t3;
      else if (eventType === "sl") triggerPrice = sl;
      else if (eventType === "tsl") triggerPrice = tsl;

      return {
        levelBlock: levels.join(" | "),
        entry,
        t1,
        t2,
        t3,
        sl,
        tsl,
        triggerPrice,
      };
    };

    const emitEvent = (
      eventType: "signal" | "tp1" | "tp2" | "tp3" | "sl" | "tsl",
      direction: "long" | "short",
      eventTime: number,
    ) => {
      if (!masterEnabled || !isAllowedForType(eventType) || eventTime <= baseline) return;
      const crossPanelKey = `${indicatorId}:${symbol}:${panel.timeframe}:${eventType}:${direction}:${eventTime}`;
      if (!shouldEmitCrossPanelStrategyAlert(crossPanelKey)) return;
      const side = direction === "long" ? "BUY" : "SELL";
      const indicatorDef = indicatorEngine.get(indicatorId);
      const indicatorName = indicatorDef?.name || indicatorId;
      const indicatorCode = indicatorDef?.shortName || indicatorId.toUpperCase();
      const timeframeLabel = getTimeframeConfig(panel.timeframe).label;
      const eventTs = formatAtEventTime(eventTime);
      const contextTag = buildStrategyContextTag();

      if (eventType === "signal") {
        const levels = buildLevelText(eventType, direction);
        const title = `${indicatorName} ${side} Entry Alert`;
        const message = `${indicatorCode} | ${indicatorName} | ${symbol} | ${timeframeLabel} | ${side} Entry${contextTag} | ${levels.levelBlock || "Levels unavailable"} | Time ${eventTs}`;
        pushAtAlertNotification(title, message, symbol);
        return;
      }

      const levels = buildLevelText(eventType, direction);
      const { entry, t1, t2, t3, sl, tsl, triggerPrice } = levels;
      let bookedPoints: number | null = null;
      if (entry != null) {
        if (eventType === "tp1" && t1 != null) bookedPoints = direction === "long" ? (t1 - entry) : (entry - t1);
        if (eventType === "tp2" && t2 != null) bookedPoints = direction === "long" ? (t2 - entry) : (entry - t2);
        if (eventType === "tp3" && t3 != null) bookedPoints = direction === "long" ? (t3 - entry) : (entry - t3);
        if (eventType === "sl" && sl != null) bookedPoints = direction === "long" ? (sl - entry) : (entry - sl);
        if (eventType === "tsl" && tsl != null) bookedPoints = direction === "long" ? (tsl - entry) : (entry - tsl);
      }
      if (bookedPoints == null) {
        bookedPoints = firstNumber([
          dashboard?.bookedPL,
          direction === "long" ? dashboard?.buyBookedPL : dashboard?.sellBookedPL,
          direction === "long" ? dashboard?.buyDayPL : dashboard?.sellDayPL,
        ]) ?? 0;
      }

      const label = eventType === "tp1"
        ? "Target1"
        : eventType === "tp2"
          ? "Target2"
          : eventType === "tp3"
            ? "Target3"
            : eventType === "tsl"
              ? "Trailing SL"
              : "Stop Loss";
        const pointsText = fmtPoints(bookedPoints);
        const triggerText = triggerPrice != null ? ` | Trigger ${fmtPrice(triggerPrice)}` : "";
        const title = `${indicatorName} ${side} ${label} Alert`;
        const message = `${indicatorCode} | ${indicatorName} | ${symbol} | ${timeframeLabel} | ${side} ${label}${contextTag}${triggerText} | Booked P&L ${pointsText} | ${levels.levelBlock || "Levels unavailable"} | Time ${eventTs}`;
      pushAtAlertNotification(title, message, symbol);
    };

    if (!strategyAlertInitializedRef.current.get(indicatorId)) {
      for (const a of arrows) {
        const et = normalizeEventType(a.type);
        seen.add(`${ctxKey}:${et}:${a.direction}:${a.time}`);
      }
      strategyAlertInitializedRef.current.set(indicatorId, true);
      strategyAlertLastStatusRef.current.set(indicatorId, String(dashboard?.status ?? ""));
      return;
    }

    const ordered = [...arrows].sort((a, b) => a.time - b.time);
    for (const a of ordered) {
      const et = normalizeEventType(a.type);
      const key = `${ctxKey}:${et}:${a.direction}:${a.time}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (seen.size > 400) {
        const first = seen.values().next().value;
        if (first) seen.delete(first);
      }
      emitEvent(et, a.direction, a.time);
    }

    const hasTypedExitMarkers = arrows.some(a => {
      const t = normalizeEventType(a.type);
      return t !== "signal";
    });
    const currentStatus = String(dashboard?.status ?? "");
    const prevStatus = strategyAlertLastStatusRef.current.get(indicatorId) || "";
    strategyAlertLastStatusRef.current.set(indicatorId, currentStatus);
    if (!hasTypedExitMarkers && currentStatus && currentStatus !== prevStatus) {
      const s = currentStatus.toLowerCase();
      let fallbackType: "tp1" | "tp2" | "tp3" | "sl" | "tsl" | null = null;
      if (s.includes("target3") || s.includes("tgt-3")) fallbackType = "tp3";
      else if (s.includes("target2") || s.includes("tgt-2")) fallbackType = "tp2";
      else if (s.includes("target1") || s.includes("tgt-1")) fallbackType = "tp1";
      else if (s.includes("trailing")) fallbackType = "tsl";
      else if (s.includes("stop") || s.includes("sl hit")) fallbackType = "sl";

      if (fallbackType) {
        const side = (String(dashboard?.signal || "").toUpperCase() === "SELL" ? "short" : "long") as "long" | "short";
        const candles = lastCandlesRef.current;
        const eventTime = candles.length >= 2 ? Number(candles[candles.length - 2]?.time ?? 0) : Math.floor(Date.now() / 1000);
        const statusKey = `${ctxKey}:status:${fallbackType}:${side}:${eventTime}`;
        if (!seen.has(statusKey)) {
          seen.add(statusKey);
          emitEvent(fallbackType, side, eventTime);
        }
      }
    }
  }, [
    activeIndicators,
    panel?.symbol?.symbol,
    panel?.timeframe,
    panelId,
    formatAtEventTime,
    pushAtAlertNotification,
  ]);

  // ─── Profit Machine Dashboard Detection ──────────────────────────
  const profitMachineActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "profit_machine" && a.visible),
    [activeIndicators],
  );

  // Apply colored candles from Profit Machine indicator metadata.
  // Only updates the color-map ref.  When the map actually changes
  // (first activation, timeframe switch, new signal), bumps
  // colorMapVersion so the main render effect re-runs with a full
  // setData redraw.  Never calls setData directly — avoids heavy
  // synchronous work inside the 300 ms polling interval.
  const applyProfitMachineCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "profit_machine";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!profitMachineActive) {
      setProfitMachineData(null);
      setPmArrowBadges([]);
      resetStrategyAlertState("profit_machine");
      // Only clear colors if this strategy owns the color map
      if (candleColorOwnerRef.current === "profit_machine") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        // Trigger a full redraw so candles revert to default colors
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("profit_machine");
      if (meta?.dashboard) {
        setProfitMachineData(meta.dashboard as ProfitMachineDashboard);
      }
      processStrategyAlertEvents("profit_machine", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyProfitMachineCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("profit_machine");
    if (meta?.dashboard) {
      setProfitMachineData(meta.dashboard as ProfitMachineDashboard);
    }
    processStrategyAlertEvents("profit_machine", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyProfitMachineCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [profitMachineActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Profit Machine arrow badge pixel positions ───────────────────
  // Recomputes the screen (x, y) for each BULLISH/BEARISH arrow badge
  // whenever the chart is scrolled / zoomed or the data changes.
  const computePmArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !profitMachineActive) {
      setPmArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("profit_machine");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setPmArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setPmArrowBadges(badges);
  }, [profitMachineActive]);

  useEffect(() => {
    if (!profitMachineActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    // Recompute on scroll / zoom
    ts.subscribeVisibleTimeRangeChange(computePmArrowBadges);
    // Also poll so new arrows (on live bars) appear
    const poll = setInterval(computePmArrowBadges, 500);
    computePmArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computePmArrowBadges); } catch (_) {}
    };
  }, [profitMachineActive, computePmArrowBadges]);

  // ─── Scalping Trend Dashboard Detection ──────────────────────────
  const scalpingTrendActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "scalping_trend" && a.visible),
    [activeIndicators],
  );

  const applyScalpingTrendCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "scalping_trend";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!scalpingTrendActive) {
      setScalpingTrendData(null);
      resetStrategyAlertState("scalping_trend");
      // Only clear colors if this strategy owns the color map
      if (candleColorOwnerRef.current === "scalping_trend") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("scalping_trend");
      if (meta?.dashboard) {
        setScalpingTrendData(meta.dashboard as ScalpingTrendDashboard);
      }
      processStrategyAlertEvents("scalping_trend", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyScalpingTrendCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("scalping_trend");
    if (meta?.dashboard) {
      setScalpingTrendData(meta.dashboard as ScalpingTrendDashboard);
    }
    processStrategyAlertEvents("scalping_trend", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyScalpingTrendCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [scalpingTrendActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Scalping Trend arrow badge pixel positions ─────────────────
  const computeStArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !scalpingTrendActive) {
      setStArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("scalping_trend");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setStArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setStArrowBadges(badges);
  }, [scalpingTrendActive]);

  useEffect(() => {
    if (!scalpingTrendActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeStArrowBadges);
    const poll = setInterval(computeStArrowBadges, 500);
    computeStArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeStArrowBadges); } catch (_) {}
    };
  }, [scalpingTrendActive, computeStArrowBadges]);

  // ─── Prime Magic Dashboard Detection ─────────────────────────────
  const primeMagicActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "prime_magic" && a.visible),
    [activeIndicators],
  );

  const applyPrimeMagicCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "prime_magic";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!primeMagicActive) {
      setPrimeMagicData(null);
      setPmgArrowBadges([]);
      resetStrategyAlertState("prime_magic");
      if (candleColorOwnerRef.current === "prime_magic") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("prime_magic");
      if (meta?.dashboard) setPrimeMagicData(meta.dashboard as PrimeMagicDashboard);
      processStrategyAlertEvents("prime_magic", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyPrimeMagicCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("prime_magic");
    if (meta?.dashboard) setPrimeMagicData(meta.dashboard as PrimeMagicDashboard);
    processStrategyAlertEvents("prime_magic", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyPrimeMagicCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [primeMagicActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Prime Magic arrow badge pixel positions ──────────────────────
  const computePmgArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !primeMagicActive) {
      setPmgArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("prime_magic");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setPmgArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setPmgArrowBadges(badges);
  }, [primeMagicActive]);

  useEffect(() => {
    if (!primeMagicActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computePmgArrowBadges);
    const poll = setInterval(computePmgArrowBadges, 500);
    computePmgArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computePmgArrowBadges); } catch (_) {}
    };
  }, [primeMagicActive, computePmgArrowBadges]);

  // ─── Max Wealth Dashboard Detection ──────────────────────────────
  const maxWealthActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "max_wealth" && a.visible),
    [activeIndicators],
  );

  const applyMaxWealthCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "max_wealth";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!maxWealthActive) {
      setMaxWealthData(null);
      setMwArrowBadges([]);
      resetStrategyAlertState("max_wealth");
      if (candleColorOwnerRef.current === "max_wealth") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("max_wealth");
      if (meta?.dashboard) setMaxWealthData(meta.dashboard as MaxWealthDashboard);
      processStrategyAlertEvents("max_wealth", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyMaxWealthCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("max_wealth");
    if (meta?.dashboard) setMaxWealthData(meta.dashboard as MaxWealthDashboard);
    processStrategyAlertEvents("max_wealth", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyMaxWealthCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [maxWealthActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Max Wealth arrow badge pixel positions ───────────────────────
  const computeMwArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !maxWealthActive) {
      setMwArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("max_wealth");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setMwArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setMwArrowBadges(badges);
  }, [maxWealthActive]);

  useEffect(() => {
    if (!maxWealthActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeMwArrowBadges);
    const poll = setInterval(computeMwArrowBadges, 500);
    computeMwArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeMwArrowBadges); } catch (_) {}
    };
  }, [maxWealthActive, computeMwArrowBadges]);

  // ─── Shadow Breaker Dashboard Detection ───────────────────────────
  const shadowBreakerActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "shadow_breaker" && a.visible),
    [activeIndicators],
  );

  const applyShadowBreakerCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "shadow_breaker";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!shadowBreakerActive) {
      setShadowBreakerData(null);
      setSbArrowBadges([]);
      resetStrategyAlertState("shadow_breaker");
      if (candleColorOwnerRef.current === "shadow_breaker") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("shadow_breaker");
      if (meta?.dashboard) setShadowBreakerData(meta.dashboard as ShadowBreakerDashboard);
      processStrategyAlertEvents("shadow_breaker", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyShadowBreakerCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("shadow_breaker");
    if (meta?.dashboard) setShadowBreakerData(meta.dashboard as ShadowBreakerDashboard);
    processStrategyAlertEvents("shadow_breaker", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyShadowBreakerCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [shadowBreakerActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Shadow Breaker arrow badge pixel positions ───────────────────
  const computeSbArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !shadowBreakerActive) {
      setSbArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("shadow_breaker");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setSbArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setSbArrowBadges(badges);
  }, [shadowBreakerActive]);

  useEffect(() => {
    if (!shadowBreakerActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeSbArrowBadges);
    const poll = setInterval(computeSbArrowBadges, 500);
    computeSbArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeSbArrowBadges); } catch (_) {}
    };
  }, [shadowBreakerActive, computeSbArrowBadges]);

  // ─── Swing Trade Dashboard Detection ─────────────────────────────
  const swingTradeActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "swing_trade" && a.visible),
    [activeIndicators],
  );

  const applySwingTradeCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "swing_trade";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!swingTradeActive) {
      setSwingTradeData(null);
      setSwArrowBadges([]);
      resetStrategyAlertState("swing_trade");
      if (candleColorOwnerRef.current === "swing_trade") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("swing_trade");
      if (meta?.dashboard) setSwingTradeData(meta.dashboard as SwingTradeDashboard);
      processStrategyAlertEvents("swing_trade", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applySwingTradeCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("swing_trade");
    if (meta?.dashboard) setSwingTradeData(meta.dashboard as SwingTradeDashboard);
    processStrategyAlertEvents("swing_trade", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applySwingTradeCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [swingTradeActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Swing Trade Arrow Badges ────────────────────────────────────
  const computeSwArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !swingTradeActive) {
      setSwArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("swing_trade");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setSwArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setSwArrowBadges(badges);
  }, [swingTradeActive]);

  useEffect(() => {
    if (!swingTradeActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeSwArrowBadges);
    const poll = setInterval(computeSwArrowBadges, 500);
    computeSwArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeSwArrowBadges); } catch (_) {}
    };
  }, [swingTradeActive, computeSwArrowBadges]);

  // ─── Trend Rover Dashboard Detection ─────────────────────────────
  // Trend Rover has NO candle coloring — just poll dashboard metadata.
  const trendRoverActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "trend_rover" && a.visible),
    [activeIndicators],
  );

  const applyTrendRoverCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "trend_rover";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!trendRoverActive) {
      setTrendRoverData(null);
      setTrArrowBadges([]);
      resetStrategyAlertState("trend_rover");
      if (candleColorOwnerRef.current === "trend_rover") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("trend_rover");
      // Always update — clears stale dashboard when first candle is still forming
      setTrendRoverData(meta?.dashboard ? meta.dashboard as TrendRoverDashboard : null);
      processStrategyAlertEvents("trend_rover", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyTrendRoverCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("trend_rover");
    // Always update — clears stale dashboard when first candle is still forming
    setTrendRoverData(meta?.dashboard ? meta.dashboard as TrendRoverDashboard : null);
    processStrategyAlertEvents("trend_rover", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyTrendRoverCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [trendRoverActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Trend Rover Arrow Badges ────────────────────────────────────
  const computeTrArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !trendRoverActive) {
      setTrArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("trend_rover");
    const arrows: { time: number; direction: "long" | "short"; price: number; type?: string; position?: string }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setTrArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short"; type?: string; position?: string }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction, type: arrow.type, position: arrow.position });
        }
      } catch (_) {}
    }
    setTrArrowBadges(badges);
  }, [trendRoverActive]);

  useEffect(() => {
    if (!trendRoverActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeTrArrowBadges);
    const poll = setInterval(computeTrArrowBadges, 500);
    computeTrArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeTrArrowBadges); } catch (_) {}
    };
  }, [trendRoverActive, computeTrArrowBadges]);

  // ─── Trend Monster Dashboard Detection ────────────────────────────
  const trendMonsterActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "trend_monster" && a.visible),
    [activeIndicators],
  );

  const applyTrendMonsterCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "trend_monster";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!trendMonsterActive) {
      setTrendMonsterData(null);
      setTmnArrowBadges([]);
      resetStrategyAlertState("trend_monster");
      if (candleColorOwnerRef.current === "trend_monster") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("trend_monster");
      if (meta?.dashboard) setTrendMonsterData(meta.dashboard as TrendMonsterDashboard);
      processStrategyAlertEvents("trend_monster", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyTrendMonsterCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("trend_monster");
    if (meta?.dashboard) setTrendMonsterData(meta.dashboard as TrendMonsterDashboard);
    processStrategyAlertEvents("trend_monster", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyTrendMonsterCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [trendMonsterActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Trend Monster Arrow Badges ─────────────────────────────────
  const computeTmnArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !trendMonsterActive) {
      setTmnArrowBadges([]); return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("trend_monster");
    const arrows: { time: number; direction: "long" | "short"; price: number; type?: string; position?: string }[] = meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setTmnArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short"; type: string; position: string }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as any);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction, type: arrow.type ?? "signal", position: arrow.position ?? (arrow.direction === "long" ? "below" : "above") });
        }
      } catch (_) {}
    }
    setTmnArrowBadges(badges);
  }, [trendMonsterActive]);

  useEffect(() => {
    if (!trendMonsterActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeTmnArrowBadges);
    const poll = setInterval(computeTmnArrowBadges, 500);
    computeTmnArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeTmnArrowBadges); } catch (_) {}
    };
  }, [trendMonsterActive, computeTmnArrowBadges]);

  // ─── Trade Mint Dashboard Detection ───────────────────────────────
  const tradeMintActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "trade_mint" && a.visible),
    [activeIndicators],
  );

  const applyTradeMintCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "trade_mint";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!tradeMintActive) {
      setTradeMintData(null);
      setTmArrowBadges([]);
      resetStrategyAlertState("trade_mint");
      if (candleColorOwnerRef.current === "trade_mint") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("trade_mint");
      if (meta?.dashboard) setTradeMintData(meta.dashboard as TradeMintDashboard);
      processStrategyAlertEvents("trade_mint", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyTradeMintCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("trade_mint");
    if (meta?.dashboard) setTradeMintData(meta.dashboard as TradeMintDashboard);
    processStrategyAlertEvents("trade_mint", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyTradeMintCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [tradeMintActive, processStrategyAlertEvents, resetStrategyAlertState]);

  // ─── Trade Mint Arrow Badges ─────────────────────────────────────
  const computeTmArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !tradeMintActive) {
      setTmArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("trade_mint");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setTmArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setTmArrowBadges(badges);
  }, [tradeMintActive]);

  useEffect(() => {
    if (!tradeMintActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeTmArrowBadges);
    const poll = setInterval(computeTmArrowBadges, 500);
    computeTmArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeTmArrowBadges); } catch (_) {}
    };
  }, [tradeMintActive, computeTmArrowBadges]);

  // ─── Price Action Dashboard Detection ─────────────────────────────
  const priceActionActive = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "price_action" && a.visible),
    [activeIndicators],
  );

  const applyPriceActionCandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "price_action";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!priceActionActive) {
      setPriceActionData(null);
      setPaArrowBadges([]);
      resetStrategyAlertState("price_action");
      if (candleColorOwnerRef.current === "price_action") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("price_action");
      if (meta?.dashboard) setPriceActionData(meta.dashboard as PriceActionDashboard);
      processStrategyAlertEvents("price_action", meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyPriceActionCandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("price_action");
    if (meta?.dashboard) setPriceActionData(meta.dashboard as PriceActionDashboard);
    processStrategyAlertEvents("price_action", meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyPriceActionCandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [priceActionActive, processStrategyAlertEvents, resetStrategyAlertState]);

  const computePaArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !priceActionActive) {
      setPaArrowBadges([]);
      return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("price_action");
    const arrows: { time: number; direction: "long" | "short"; price: number }[] =
      meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setPaArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short" }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as UTCTimestamp);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction });
        }
      } catch (_) {}
    }
    setPaArrowBadges(badges);
  }, [priceActionActive]);

  useEffect(() => {
    if (!priceActionActive || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computePaArrowBadges);
    const poll = setInterval(computePaArrowBadges, 500);
    computePaArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computePaArrowBadges); } catch (_) {}
    };
  }, [priceActionActive, computePaArrowBadges]);

  // ─── Alpha Trade V2 Dashboard Detection ───────────────────────────
  const alphaTradeV2Active = useMemo(
    () => activeIndicators.some(a => a.indicatorId === "alpha_trade_v2" && a.visible),
    [activeIndicators],
  );

  const alphaTradeV2Instance = useMemo(
    () => activeIndicators.find(a => a.indicatorId === "alpha_trade_v2" && a.visible) || null,
    [activeIndicators],
  );

  const atv2AlertEnabled = Boolean(alphaTradeV2Instance?.params?.alertEnabled ?? true);

  const formatAtv2EventTime = useCallback((ts: number) => {
    // Indicator event times are bar timestamps; shift to IST for display consistency.
    const date = new Date((ts + IST_OFFSET) * 1000);
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    const hh = String(date.getUTCHours()).padStart(2, "0");
    const min = String(date.getUTCMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }, [IST_OFFSET]);

  const playAtv2AlertSound = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.45);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.45);
    } catch {
      // Sound unavailable on this platform/browser — ignore.
    }
  }, []);

  const sendAtv2OSNotification = useCallback(async (title: string, body: string) => {
    try {
      const { sendNotification, isPermissionGranted, requestPermission } =
        await import("@tauri-apps/plugin-notification");
      let permitted = await isPermissionGranted();
      if (!permitted) {
        const result = await requestPermission();
        permitted = result === "granted";
      }
      if (permitted) {
        sendNotification({ title, body });
        return;
      }
    } catch {
      // Tauri notification plugin unavailable — try browser API.
    }

    try {
      if ("Notification" in window) {
        if (Notification.permission === "granted") {
          new Notification(title, { body });
        } else if (Notification.permission !== "denied") {
          const perm = await Notification.requestPermission();
          if (perm === "granted") {
            new Notification(title, { body });
          }
        }
      }
    } catch {
      // Ignore when system notifications are not available.
    }
  }, []);

  const pushAtv2AlertNotification = useCallback((title: string, message: string, symbol: string) => {
    dispatch(addNotification({
      type: "alert",
      title,
      message,
      symbol,
    }));
    toast(`🔔 ${title}`, { duration: 6000 });
    void sendAtv2OSNotification(title, message);
    if (alertSoundEnabled) {
      playAtv2AlertSound();
    }
  }, [dispatch, sendAtv2OSNotification, alertSoundEnabled, playAtv2AlertSound]);

  const processAtv2AlertEvents = useCallback((meta: any) => {
    const symbol = panel?.symbol?.symbol;
    if (!symbol || !panel?.timeframe) return;

    const ctxKey = `${panelId}:${symbol}:${panel.timeframe}`;
    if (atv2AlertContextRef.current !== ctxKey) {
      atv2AlertContextRef.current = ctxKey;
      atv2AlertInitializedRef.current = false;
      atv2SeenEventKeysRef.current.clear();
      atv2AlertBaselineTimeRef.current = null;
    }

    const arrows: Array<{ time: number; direction: "long" | "short"; type?: string }> =
      Array.isArray(meta?.arrowMarkers) ? meta.arrowMarkers : [];

    if (atv2AlertBaselineTimeRef.current == null) {
      const candles = lastCandlesRef.current;
      const closedBarTime = candles.length >= 2 ? Number(candles[candles.length - 2]?.time ?? 0) : 0;
      const liveBarTime = candles.length >= 1 ? Number(candles[candles.length - 1]?.time ?? 0) : 0;
      atv2AlertBaselineTimeRef.current = Math.max(closedBarTime, liveBarTime, Math.floor(Date.now() / 1000));
    }

    // Prevent historical flood: baseline all current markers the first time we attach.
    if (!atv2AlertInitializedRef.current) {
      for (const a of arrows) {
        const t = a.type ?? "signal";
        atv2SeenEventKeysRef.current.add(`${ctxKey}:${t}:${a.direction}:${a.time}`);
      }
      atv2AlertInitializedRef.current = true;
      return;
    }

    const timeframeLabel = getTimeframeConfig(panel.timeframe).label;
    const dash = meta?.dashboard as AlphaTradeV2Dashboard | undefined;
    const ordered = [...arrows].sort((a, b) => a.time - b.time);

    for (const a of ordered) {
      const eventType = (a.type ?? "signal").toLowerCase();
      const eventKey = `${ctxKey}:${eventType}:${a.direction}:${a.time}`;
      if (atv2SeenEventKeysRef.current.has(eventKey)) continue;

      atv2SeenEventKeysRef.current.add(eventKey);
      // Bound memory for long-running sessions.
      if (atv2SeenEventKeysRef.current.size > 400) {
        const first = atv2SeenEventKeysRef.current.values().next().value;
        if (first) atv2SeenEventKeysRef.current.delete(first);
      }

      if (!atv2AlertEnabled) continue;

      // Hard gate: only emit alerts for events strictly after open/baseline time.
      if (a.time <= (atv2AlertBaselineTimeRef.current ?? 0)) continue;

      const crossPanelKey = `alpha_trade_v2:${symbol}:${panel.timeframe}:${eventType}:${a.direction}:${a.time}`;
      if (!shouldEmitCrossPanelStrategyAlert(crossPanelKey)) continue;

      const ts = formatAtv2EventTime(a.time);
      const signalSide = a.direction === "long" ? "BUY" : "SELL";
      const entry = dash?.entry ?? null;
      const sl = dash?.stopLoss ?? null;
      const t1 = dash?.target1 ?? null;
      const t2 = dash?.target2 ?? null;
      const fmt = (n: number | null) => n != null ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-";

      if (eventType === "signal") {
        const title = `ALPHA TRADE ${signalSide} Signal`;
        const message = `ALPHA TRADE | ${symbol} | ${timeframeLabel} | ${signalSide} @ ${fmt(entry)} | T1 ${fmt(t1)} | T2 ${fmt(t2)} | SL ${fmt(sl)} | Signal Time ${ts}`;
        pushAtv2AlertNotification(title, message, symbol);
        continue;
      }

      if (eventType === "tp1" || eventType === "tp2" || eventType === "sl") {
        let bookedPoints = dash?.bookedPL ?? 0;
        if (entry != null) {
          if (eventType === "tp1" && t1 != null) {
            bookedPoints = a.direction === "long" ? (t1 - entry) : (entry - t1);
          } else if (eventType === "tp2" && t2 != null) {
            bookedPoints = a.direction === "long" ? (t2 - entry) : (entry - t2);
          } else if (eventType === "sl" && sl != null) {
            bookedPoints = a.direction === "long" ? (sl - entry) : (entry - sl);
          }
        }

        const eventLabel = eventType === "tp1" ? "Target1" : eventType === "tp2" ? "Target2" : "Stop Loss";
        const pointsText = `${bookedPoints >= 0 ? "+" : ""}${bookedPoints.toFixed(2)} pts`;
        const title = `ALPHA TRADE ${eventLabel} Triggered`;
        const message = `ALPHA TRADE | ${symbol} | ${timeframeLabel} | ${eventLabel} Triggered | Booked P&L ${pointsText} | Triggered Time ${ts}`;
        pushAtv2AlertNotification(title, message, symbol);
      }
    }
  }, [
    panel?.symbol?.symbol,
    panel?.timeframe,
    panelId,
    atv2AlertEnabled,
    formatAtv2EventTime,
    pushAtv2AlertNotification,
  ]);

  const applyAlphaTradeV2CandleColors = useCallback((candleColors: { time: number; color: string }[]) => {
    const colorMap = new Map<number, string>();
    for (const cc of candleColors) {
      colorMap.set((cc.time + IST_OFFSET) as number, cc.color);
    }
    const prevMap = candleColorMapRef.current;
    const prevFirst = prevMap.size > 0 ? prevMap.keys().next().value : -1;
    const newFirst = colorMap.size > 0 ? colorMap.keys().next().value : -1;
    const colorsChanged = colorMap.size !== prevMap.size || newFirst !== prevFirst;
    candleColorMapRef.current = colorMap;
    candleColorOwnerRef.current = "alpha_trade_v2";
    if (colorsChanged) {
      needsColorRedrawRef.current = true;
      setColorMapVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    if (!alphaTradeV2Active) {
      setAlphaTradeV2Data(null);
      setAtv2ArrowBadges([]);
      atv2AlertInitializedRef.current = false;
      atv2SeenEventKeysRef.current.clear();
      atv2AlertContextRef.current = "";
      atv2AlertBaselineTimeRef.current = null;
      if (candleColorOwnerRef.current === "alpha_trade_v2") {
        candleColorOwnerRef.current = null;
        candleColorMapRef.current = new Map();
        needsColorRedrawRef.current = true;
        setColorMapVersion(v => v + 1);
      }
      return;
    }
    const interval = setInterval(() => {
      const meta = indicatorRendererRef.current?.getMetadata("alpha_trade_v2");
      if (meta?.dashboard) setAlphaTradeV2Data(meta.dashboard as AlphaTradeV2Dashboard);
      processAtv2AlertEvents(meta);
      if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
        applyAlphaTradeV2CandleColors(meta.candleColors);
      }
    }, 300);
    const meta = indicatorRendererRef.current?.getMetadata("alpha_trade_v2");
    if (meta?.dashboard) setAlphaTradeV2Data(meta.dashboard as AlphaTradeV2Dashboard);
    processAtv2AlertEvents(meta);
    if (meta?.candleColors && candleSeriesRef.current && lastCandlesRef.current.length > 0 && currentChartTypeRef.current === "candlestick") {
      applyAlphaTradeV2CandleColors(meta.candleColors);
    }
    return () => clearInterval(interval);
  }, [alphaTradeV2Active, processAtv2AlertEvents]);

  // ─── Alpha Trade V2 Arrow Badges ────────────────────────────
  const computeAtv2ArrowBadges = useCallback(() => {
    if (!chartRef.current || !candleSeriesRef.current || !alphaTradeV2Active) {
      setAtv2ArrowBadges([]); return;
    }
    const meta = indicatorRendererRef.current?.getMetadata("alpha_trade_v2");
    const arrows: { time: number; direction: "long" | "short"; price: number; type?: string; position?: string }[] = meta?.arrowMarkers ?? [];
    if (arrows.length === 0) { setAtv2ArrowBadges([]); return; }
    const badges: { x: number; y: number; direction: "long" | "short"; type: string; position: string }[] = [];
    for (const arrow of arrows) {
      try {
        const x = chartRef.current!.timeScale().timeToCoordinate((arrow.time + IST_OFFSET) as any);
        const y = candleSeriesRef.current!.priceToCoordinate(arrow.price);
        if (x !== null && y !== null) {
          badges.push({ x, y, direction: arrow.direction, type: arrow.type ?? "signal", position: arrow.position ?? (arrow.direction === "long" ? "below" : "above") });
        }
      } catch (_) {}
    }
    setAtv2ArrowBadges(badges);
  }, [alphaTradeV2Active]);

  useEffect(() => {
    if (!alphaTradeV2Active || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeAtv2ArrowBadges);
    const poll = setInterval(computeAtv2ArrowBadges, 500);
    computeAtv2ArrowBadges();
    return () => {
      clearInterval(poll);
      try { ts.unsubscribeVisibleTimeRangeChange(computeAtv2ArrowBadges); } catch (_) {}
    };
  }, [alphaTradeV2Active, computeAtv2ArrowBadges]);

  // ─── Indicator Callbacks ──────────────────────────────────────────
  const handleAddIndicator = useCallback((indicatorId: string) => {
    const instance = indicatorEngine.createInstance(indicatorId);
    if (instance) {
      indicatorNeedsFullRecalcRef.current = true;
      setActiveIndicators(prev => [...prev, instance]);
    }
  }, []);

  const handleRemoveIndicator = useCallback((instanceId: string) => {
    indicatorNeedsFullRecalcRef.current = true;
    setActiveIndicators(prev => prev.filter(a => a.instanceId !== instanceId));
  }, []);

  const handleRemoveAllIndicators = useCallback(() => {
    indicatorNeedsFullRecalcRef.current = true;
    setActiveIndicators([]);
  }, []);

  const handleToggleIndicatorVisibility = useCallback((instanceId: string) => {
    indicatorNeedsFullRecalcRef.current = true;
    setActiveIndicators(prev =>
      prev.map(a => a.instanceId === instanceId ? { ...a, visible: !a.visible } : a)
    );
  }, []);

  // Debounced param change: accumulates rapid edits (typing, slider drag)
  const pendingParamsRef = useRef<Map<string, { params: Record<string, any>; timer: ReturnType<typeof setTimeout> }>>(new Map());

  const handleIndicatorParamsChange = useCallback((instanceId: string, params: Record<string, any>) => {
    // Mark forceFullRecalc BEFORE the state update so the resulting effect
    // fires with delay=0 instead of the 50 ms debounce delay.  Without this,
    // on active markets where live ticks arrive faster than 50 ms, the
    // indicator-update timer gets reset on every tick and the preset / param
    // change never actually reaches the renderer.
    indicatorNeedsFullRecalcRef.current = true;

    // Update React state immediately for responsive UI
    setActiveIndicators(prev =>
      prev.map(a => a.instanceId === instanceId ? { ...a, params } : a)
    );

    // Keep a debounce timer as a safety-net for rapid sequential changes
    // (e.g. dragging a number slider).  The immediate flag above already
    // triggers the first recalc; this ensures a final pass after the user
    // stops interacting.
    const existing = pendingParamsRef.current.get(instanceId);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      indicatorNeedsFullRecalcRef.current = true;
      // Force re-render to trigger the indicator effect
      setActiveIndicators(prev => [...prev]);
      pendingParamsRef.current.delete(instanceId);
    }, 300);

    pendingParamsRef.current.set(instanceId, { params, timer });
  }, []);

  // Apply a custom scripted indicator to the chart
  const handleApplyCustomIndicator = useCallback((def: IndicatorDefinition) => {
    // Register the custom indicator definition in the engine
    indicatorEngine.register(def);
    // Then create an instance and add it to the chart
    const instance = indicatorEngine.createInstance(def.id);
    if (instance) {
      indicatorNeedsFullRecalcRef.current = true;
      setActiveIndicators(prev => [...prev, instance]);
    }
  }, []);

  // Apply a saved template: replaces all indicators and chart type
  const handleApplyTemplate = useCallback((template: { chartType: string; indicators: Array<{ indicatorId: string; params: Record<string, any>; visible: boolean }>; visibleBarsCount?: number }) => {
    dispatch(setChartType({ panelId: panelId, chartType: template.chartType as ChartType }));
    const newIndicators: ActiveIndicator[] = template.indicators.map(t => ({
      instanceId: `${t.indicatorId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      indicatorId: t.indicatorId,
      params: t.params,
      visible: t.visible,
    }));
    indicatorNeedsFullRecalcRef.current = true;
    setActiveIndicators(newIndicators);

    // Restore the zoom level from when the template was saved
    if (template.visibleBarsCount && template.visibleBarsCount > 0 && chartRef.current) {
      const dataLen = lastCandlesRef.current.length;
      if (dataLen > 0) {
        const MIN_VIS = 50;
        const barsToShow = Math.max(MIN_VIS, Math.min(dataLen, template.visibleBarsCount));
        chartRef.current.timeScale().setVisibleLogicalRange({
          from: dataLen - barsToShow,
          to: dataLen + 5,
        });
      }
    }
  }, [dispatch, panelId]);

  // ─── Auto-apply default template on first mount ───────────────
  const templateAppliedRef = useRef(false);
  const templateVisibleBarsRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (templateAppliedRef.current) return;
    templateAppliedRef.current = true;
    const defaultTemplate = getDefaultTemplate();
    if (!defaultTemplate) return;
    dispatch(setChartType({ panelId: panelId, chartType: defaultTemplate.chartType as ChartType }));
    if (defaultTemplate.visibleBarsCount && defaultTemplate.visibleBarsCount > 0) {
      templateVisibleBarsRef.current = defaultTemplate.visibleBarsCount;
    }
    if (defaultTemplate.indicators.length > 0) {
      const newIndicators: ActiveIndicator[] = defaultTemplate.indicators.map(t => ({
        instanceId: `${t.indicatorId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        indicatorId: t.indicatorId,
        params: t.params,
        visible: t.visible,
      }));
      indicatorNeedsFullRecalcRef.current = true;
      setActiveIndicators(newIndicators);
    }
  }, [dispatch, panelId]);

  // ─── Bar Replay Handlers ──────────────────────────────────────
  const replayStopTimer = useCallback(() => {
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
  }, []);

  const replayEnter = useCallback(() => {
    // Snapshot current candles as the replay data source
    const candles = lastCandlesRef.current;
    if (candles.length < 10) return;
    replayAllCandlesRef.current = [...candles];
    setIsReplayMode(true);
    setReplayBar(20); // start from bar 20 to have some initial data
    setReplayPlaying(false);
    replayStopTimer();
  }, [replayStopTimer]);

  const replayExit = useCallback(() => {
    replayStopTimer();
    setIsReplayMode(false);
    setReplayPlaying(false);
    setReplayBar(0);
    replayAllCandlesRef.current = [];
  }, [replayStopTimer]);

  const replayStepForward = useCallback(() => {
    setReplayBar(prev => {
      const next = prev + 1;
      if (next >= replayAllCandlesRef.current.length) {
        replayStopTimer();
        setReplayPlaying(false);
        return replayAllCandlesRef.current.length;
      }
      return next;
    });
  }, [replayStopTimer]);

  const replayStepBack = useCallback(() => {
    setReplayBar(prev => Math.max(1, prev - 1));
  }, []);

  const replayPlay = useCallback(() => {
    setReplayPlaying(true);
    replayStopTimer();
    replayTimerRef.current = setInterval(() => {
      setReplayBar(prev => {
        const next = prev + 1;
        if (next >= replayAllCandlesRef.current.length) {
          replayStopTimer();
          setReplayPlaying(false);
          return replayAllCandlesRef.current.length;
        }
        return next;
      });
    }, replaySpeed);
  }, [replaySpeed, replayStopTimer]);

  const replayPause = useCallback(() => {
    setReplayPlaying(false);
    replayStopTimer();
  }, [replayStopTimer]);

  const replayReset = useCallback(() => {
    replayStopTimer();
    setReplayPlaying(false);
    setReplayBar(20);
  }, [replayStopTimer]);

  const replaySeek = useCallback((bar: number) => {
    setReplayBar(Math.max(1, Math.min(bar, replayAllCandlesRef.current.length)));
  }, []);

  const replaySpeedChange = useCallback((speed: number) => {
    setReplaySpeed(speed);
    if (replayPlaying) {
      replayStopTimer();
      replayTimerRef.current = setInterval(() => {
        setReplayBar(prev => {
          const next = prev + 1;
          if (next >= replayAllCandlesRef.current.length) {
            replayStopTimer();
            setReplayPlaying(false);
            return replayAllCandlesRef.current.length;
          }
          return next;
        });
      }, speed);
    }
  }, [replayPlaying, replayStopTimer]);

  // Cleanup replay timer on unmount
  useEffect(() => {
    return () => replayStopTimer();
  }, [replayStopTimer]);

  // ─── Compare Symbol Handlers ──────────────────────────────────────
  // Compare series share the SAME price axis as the main series so both
  // symbols' real (un-normalized) prices appear on a single right-side
  // price scale.  LWC auto-fits the visible range to encompass both.
  const mainScaleId = chartSettings.priceScalePosition || "right";
  const COMPARE_COLORS = ["#ff9800", "#9c27b0", "#00bcd4", "#e91e63", "#4caf50", "#ff5722"];

  const barsToLineData = useCallback(
    (bars: OneMinuteBar[]) => {
      if (!bars || bars.length === 0) return [];
      const candles: CandleData[] = chartDataHandler.convertTimeframe(bars, panel?.timeframe || "1m");
      return candles.map((c) => ({
        time: (c.time + IST_OFFSET) as UTCTimestamp,
        value: c.close,
      }));
    },
    [panel?.timeframe],
  );

  const handleCompareAdd = useCallback(
    (sym: ChartSymbol) => {
      if (!chartRef.current) return;
      const chart = chartRef.current;

      const color = COMPARE_COLORS[compareColorIndexRef.current % COMPARE_COLORS.length];
      compareColorIndexRef.current++;

      const series = chart.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        priceScaleId: mainScaleId,
        lastValueVisible: true,
        priceLineVisible: false,
        title: sym.symbol,
      });

      const newEntry: CompareEntry = {
        symbol: sym,
        color,
        series,
        bars: [],
        loading: true,
      };

      setCompareEntries((prev) => [...prev, newEntry]);

      const controller = new AbortController();
      compareAbortControllersRef.current.set(sym.symbol, controller);

      const scripId = sym.id;
      if (!scripId) {
        console.warn(`[Compare] No scrip ID for ${sym.symbol}, cannot fetch data`);
        setCompareEntries((prev) =>
          prev.map((e) => (e.symbol.symbol === sym.symbol ? { ...e, loading: false } : e)),
        );
        return;
      }

      (async () => {
        try {
          const todayBars = await chartBackfillHandler.fetchTodayData(scripId, sym.symbol);
          if (controller.signal.aborted) return;

          if (todayBars.length > 0) {
            setCompareEntries((prev) =>
              prev.map((e) => {
                if (e.symbol.symbol !== sym.symbol) return e;
                const data = barsToLineData(todayBars);
                if (data.length > 0) {
                  try { e.series.setData(data); } catch {}
                }
                return { ...e, bars: todayBars };
              }),
            );
          }

          const { bars: historyBars } = await chartBackfillHandler.fetchHistoryData(scripId, sym.symbol);
          if (controller.signal.aborted) return;

          setCompareEntries((prev) =>
            prev.map((e) => {
              if (e.symbol.symbol !== sym.symbol) return e;
              const barMap = new Map<number, OneMinuteBar>();
              historyBars.forEach((b) => barMap.set(b.timestamp, b));
              e.bars.forEach((b) => barMap.set(b.timestamp, b));
              const merged = Array.from(barMap.values()).sort((a, b) => a.timestamp - b.timestamp);
              const data = barsToLineData(merged);
              if (data.length > 0) {
                try { e.series.setData(data); } catch {}
              }
              return { ...e, bars: merged, loading: false };
            }),
          );
          // Start receiving live WebSocket ticks for this compare symbol now
          // that historical data is fully loaded and rendered on the series.
          dispatch(addCompareSymbol(sym.symbol));
        } catch (err: any) {
          if (!controller.signal.aborted) {
            console.error(`[Compare] Failed to fetch data for ${sym.symbol}:`, err.message);
            setCompareEntries((prev) =>
              prev.map((e) =>
                e.symbol.symbol === sym.symbol ? { ...e, loading: false } : e,
              ),
            );
            // Still subscribe to live ticks even if backfill failed — today's
            // data may already be on the series from the earlier todayBars fetch.
            dispatch(addCompareSymbol(sym.symbol));
          }
        }
      })();
    },
    [barsToLineData, dispatch],
  );

  const handleCompareRemove = useCallback(
    (symbol: string) => {
      const controller = compareAbortControllersRef.current.get(symbol);
      if (controller) {
        controller.abort();
        compareAbortControllersRef.current.delete(symbol);
      }

      setCompareEntries((prev) => {
        const entry = prev.find((e) => e.symbol.symbol === symbol);
        if (entry && chartRef.current) {
          try {
            chartRef.current.removeSeries(entry.series);
          } catch {}
        }
        return prev.filter((e) => e.symbol.symbol !== symbol);
      });

      // Unsubscribe from live ticks for this compare symbol (also cleans up
      // oneMinuteData unless the symbol is still used in a main panel).
      dispatch(removeCompareSymbol(symbol));
    },
    [dispatch],
  );

  // Re-render compare series when timeframe changes
  useEffect(() => {
    for (const entry of compareEntries) {
      if (entry.bars.length > 0) {
        const data = barsToLineData(entry.bars);
        if (data.length > 0) {
          try { entry.series.setData(data); } catch {}
        }
      }
    }
  }, [panel?.timeframe, compareEntries.length]);

  // ── Live tick updates for compare series ──────────────────────
  // Fires whenever a compare symbol's last bar changes in Redux
  // (i.e., a WebSocket tick arrived for that symbol).  Uses series.update()
  // for a lightweight incremental push instead of a full setData() redraw.
  useEffect(() => {
    if (compareEntries.length === 0) return;
    for (const entry of compareEntries) {
      if (entry.loading) continue; // don't update while initial data is still loading
      const lastBar = compareLastBars[entry.symbol.symbol];
      if (!lastBar) continue;
      const linePoint = {
        time: (lastBar.timestamp + IST_OFFSET) as UTCTimestamp,
        value: lastBar.close,
      };
      try { entry.series.update(linePoint); } catch {}
    }
  }, [compareLastBars]); // eslint-disable-line

  const handleRemovePanel = () => {
    dispatch(removePanel(panelId));
  };

  // ─── Per-Pane Background Helpers ────────────────────────────────
  const getEffectiveBgPreset = useCallback((): ChartBgPreset => {
    if (paneBgId) {
      const found = CHART_BG_PRESETS.find(p => p.id === paneBgId);
      if (found) return found;
    }
    return loadChartBgPreset();
  }, [paneBgId]);

  const applyBgToChart = useCallback((preset: ChartBgPreset) => {
    if (!chartRef.current) return;
    const settings = loadChartSettings();
    const gridVisible = settings.gridVisible;
    chartRef.current.applyOptions({
      layout: {
        background: { color: preset.bg },
        textColor: preset.textColor,
      },
      grid: {
        vertLines: { color: gridVisible ? preset.gridColor : "transparent" },
        horzLines: { color: gridVisible ? preset.gridColor : "transparent" },
      },
      rightPriceScale: { borderColor: preset.borderColor },
      leftPriceScale:  { borderColor: preset.borderColor },
      timeScale:       { borderColor: preset.borderColor },
    });
  }, []);

  const handlePaneBgSelect = useCallback((preset: ChartBgPreset) => {
    setPaneBgId(preset.id);
    savePaneBg(panelId, preset.id);
    applyBgToChart(preset);
  }, [panelId, applyBgToChart]);

  const handlePaneBgReset = useCallback(() => {
    setPaneBgId(null);
    clearPaneBg(panelId);
    applyBgToChart(loadChartBgPreset());
  }, [panelId, applyBgToChart]);

  const handleScrollToLatest = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    // scrollToPosition scrolls so that the last data point is at the given
    // bar-offset from the right edge. Using the configured rightOffset keeps
    // the same look the user normally sees. This is far more reliable than
    // scrollToRealTime() which depends on internal auto-scroll state.
    // Reset to default right-side offset when explicitly scrolling to latest.
    userRightOffsetRef.current = 5;
    isProgrammaticScrollRef.current = true;
    // Reset zoom to show the last ~150 bars (same as initial fit) so
    // the user sees properly-sized candles instead of a single tiny bar.
    const dataLen = lastCandleCountRef.current;
    if (dataLen > 0) {
      const DEFAULT_VISIBLE_BARS = templateVisibleBarsRef.current ?? (isMobile ? 60 : 150);
      const MIN_RANGE = 50;
      const barsToShow = Math.max(MIN_RANGE, Math.min(dataLen, DEFAULT_VISIBLE_BARS));
      if (dataLen >= barsToShow) {
        try {
          ts.setVisibleLogicalRange({
            from: dataLen - barsToShow,
            to: dataLen - 1 + 5,
          });
        } catch {}
      }
    }
    ts.scrollToPosition(5, false);
    // Defer unlocking the programmatic guard: setVisibleLogicalRange +
    // scrollToPosition queue range-change events in the next paint frame.
    // With separate-pane indicators, LWC's layout can cascade across 2+
    // frames.  Double-rAF ensures the guard is held through all cascades.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    });
    // Sync chart's rightOffset back to default
    try { ts.applyOptions({ rightOffset: 5 }); } catch {}
    // Re-enable auto-tracking so live ticks keep the chart following the price
    isUserScrolledAwayRef.current = false;
    // Explicitly re-enable autoScale so the price axis fits the visible data.
    // User pressed "scroll to latest" so we intentionally reset their Y adjustment.
    const scaleId = chartSettings.priceScalePosition || "right";
    try {
      chart.priceScale(scaleId).applyOptions({ autoScale: true });
    } catch (_) {}
  }, [chartSettings.priceScalePosition]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when the user is typing in an input/textarea/select or contentEditable
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "End") {
        e.preventDefault();
        handleScrollToLatest();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        handleZoomOut();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleScrollToLatest, handleZoomIn, handleZoomOut]);

  const handleScreenshot = useCallback(async () => {
    if (!chartPanelRef.current) return;
    try {
      // Use html2canvas to capture the entire chart panel including all overlays
      const canvas = await html2canvas(chartPanelRef.current, {
        backgroundColor: null,
        scale: 2, // Higher resolution
        logging: false,
        useCORS: true,
      });
      
      canvas.toBlob((blob) => {
        if (!blob) {
          window.dispatchEvent(new CustomEvent("show-toast", {
            detail: { message: "Screenshot failed: no image data", type: "error" }
          }));
          return;
        }
        // Copy to clipboard
        navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]).then(() => {
          // Success toast
          window.dispatchEvent(new CustomEvent("show-toast", {
            detail: { message: "Screenshot copied to clipboard", type: "success" }
          }));
        }).catch(() => {
          // Fallback: download as file
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${panel?.symbol?.symbol || "chart"}_${panel?.timeframe || ""}_${new Date().toISOString().slice(0, 10)}.png`;
          a.click();
          URL.revokeObjectURL(url);
          // Show download toast instead
          window.dispatchEvent(new CustomEvent("show-toast", {
            detail: { message: "Screenshot downloaded", type: "success" }
          }));
        });
      }, "image/png");
    } catch (err) {
      console.warn("[Chart] Screenshot failed", err);
      window.dispatchEvent(new CustomEvent("show-toast", {
        detail: { message: "Screenshot failed", type: "error" }
      }));
    }
  }, [panel?.symbol?.symbol, panel?.timeframe]);

  // ─── Expose imperative handle for parent (GlobalChartToolbar) ──
  useImperativeHandle(ref, () => ({
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    scrollToLatest: handleScrollToLatest,
    takeScreenshot: handleScreenshot,
    addIndicator: handleAddIndicator,
    removeIndicator: handleRemoveIndicator,
    removeAllIndicators: handleRemoveAllIndicators,
    toggleIndicatorVisibility: handleToggleIndicatorVisibility,
    changeIndicatorParams: handleIndicatorParamsChange,
    getActiveIndicators: () => activeIndicators,
    getVisibleBarsCount: () => {
      if (!chartRef.current) return 150;
      try {
        const lr = chartRef.current.timeScale().getVisibleLogicalRange();
        if (lr) return Math.round(lr.to - lr.from);
      } catch {}
      return 150;
    },
    getDrawingCount: () => drawings.length,
    clearAllDrawings: handleClearAllDrawings,
    undoDrawing: handleUndoDrawing,
    toggleAlerts: () => setIsAlertPanelOpen(prev => !prev),
    toggleStrategy: () => setIsStrategyOpen(prev => !prev),
    toggleCustomIndicator: () => setIsCustomIndicatorOpen(prev => !prev),
    toggleReplay: () => isReplayMode ? replayExit() : replayEnter(),
    toggleCompare: () => setIsCompareOpen(prev => !prev),
    toggleGrid: (visible: boolean) => {
      if (!chartRef.current) return;
      const gridColor = visible ? (colorTheme === "dark" ? "#2a2e39" : "#e1e3eb") : "transparent";
      chartRef.current.applyOptions({
        grid: {
          vertLines: { color: gridColor },
          horzLines: { color: gridColor },
        },
      });
    },
    applyCustomIndicator: handleApplyCustomIndicator,
    applyTemplate: handleApplyTemplate,
  }));

  if (!panel) return null;

  return (
    <div
      ref={chartPanelRef}
      className={`chart-panel ${colorTheme}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Pane header — symbol + timeframe on left, actions on right in one flex row */}
      <div className="pane-header" onContextMenu={handlePaneContextMenu}>
        <div className="symbol-selector">
          <span className="symbol-label">
            {panel.symbol ? panel.symbol.symbol : "Welcome"}
            {panel.symbol?.segment && <span className="segment-badge">({panel.symbol.segment})</span>}
          </span>
        </div>
        {panel.symbol && <span className="pane-timeframe-badge">{getTimeframeConfig(panel.timeframe).label}</span>}
        {panel.isLoading && <span className="pane-loading-dot" />}

        {/* Active badge + Background color + Close button — inline in header, no overlap */}
        <div className="pane-top-right-actions">
          {isActive && !isTabLayout && <span className="pane-active-badge">Active</span>}
          {panel.symbol && <button
            ref={paneBgBtnRef}
            className="pane-bg-btn"
            onClick={() => setPaneBgPickerOpen(prev => !prev)}
            title="Pane background color"
          >
            <svg viewBox="0 0 20 20" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <path d="M10 2 L10 18" stroke="currentColor" strokeWidth="1.2" />
              <rect x="2" y="2" width="8" height="16" rx="3" fill="currentColor" opacity="0.5" />
            </svg>
            <span className="pane-bg-btn__label">Theme</span>
          </button>}
          <button className="pane-close-btn" onClick={handleRemovePanel} title="Remove Panel">
            <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop-only right-click context menu */}
      {paneContextMenu && (
        <div
          className={`pane-context-menu ${colorTheme}`}
          // eslint-disable-next-line react/forbid-dom-props
          style={{
            left: Math.min(paneContextMenu.x, window.innerWidth - 120),
            ...(paneContextMenu.y + 80 > window.innerHeight
              ? { bottom: window.innerHeight - paneContextMenu.y }
              : { top: paneContextMenu.y }),
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <button
            className="pane-context-menu-item pane-context-menu-close"
            onClick={() => { setPaneContextMenu(null); dispatch(removePanel(panelId)); }}
          >
            Close
          </button>
        </div>
      )}

      {/* Per-pane background picker popup */}
      {paneBgPickerOpen && (
        <div className="pane-bg-picker-overlay" onClick={() => setPaneBgPickerOpen(false)}>
          <div
            className={`pane-bg-picker ${colorTheme}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="pane-bg-picker-header">
              <span className="pane-bg-picker-title">Pane Background</span>
              <button className="pane-bg-picker-close" onClick={() => setPaneBgPickerOpen(false)} title="Close">
                <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="4" y1="4" x2="16" y2="16" />
                  <line x1="16" y1="4" x2="4" y2="16" />
                </svg>
              </button>
            </div>

            <p className="pane-bg-picker-desc">Choose a background color for this chart pane. The foreground colors (text, grid, scales) will automatically adapt for maximum readability.</p>

            {paneBgId && (
              <button className="pane-bg-reset-btn" onClick={handlePaneBgReset}>
                ↩ Reset to global
              </button>
            )}

            <div className="pane-bg-section-label">Dark</div>
            <div className="pane-bg-grid">
              {CHART_BG_PRESETS.filter(p => p.category === "dark").map(preset => (
                <button
                  key={preset.id}
                  className={`ctf-bg-swatch ${paneBgId === preset.id ? "ctf-bg-swatch-active" : ""}`}
                  onClick={() => { handlePaneBgSelect(preset); setPaneBgPickerOpen(false); }}
                  title={preset.label}
                >
                  <span
                    className="ctf-bg-swatch-color"
                    // eslint-disable-next-line react/forbid-dom-props
                    style={{ ["--swatch-bg" as string]: preset.bg } as React.CSSProperties}
                  >
                    {paneBgId === preset.id && (
                      <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,7 6,11 12,3" />
                      </svg>
                    )}
                  </span>
                  <span className="ctf-bg-swatch-label">{preset.label}</span>
                </button>
              ))}
            </div>

            <div className="pane-bg-section-label">Light</div>
            <div className="pane-bg-grid">
              {CHART_BG_PRESETS.filter(p => p.category === "light").map(preset => (
                <button
                  key={preset.id}
                  className={`ctf-bg-swatch ${paneBgId === preset.id ? "ctf-bg-swatch-active" : ""}`}
                  onClick={() => { handlePaneBgSelect(preset); setPaneBgPickerOpen(false); }}
                  title={preset.label}
                >
                  <span
                    className={`ctf-bg-swatch-color ${preset.category === "light" ? "ctf-bg-swatch-light" : ""}`}
                    // eslint-disable-next-line react/forbid-dom-props
                    style={{ ["--swatch-bg" as string]: preset.bg } as React.CSSProperties}
                  >
                    {paneBgId === preset.id && (
                      <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="#333" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,7 6,11 12,3" />
                      </svg>
                    )}
                  </span>
                  <span className="ctf-bg-swatch-label">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Price Alert Panel */}
      {isAlertPanelOpen && panel.symbol && (
        <PriceAlertPanel
          symbol={panel.symbol.symbol}
          currentPrice={lastCandlesRef.current.length > 0 ? lastCandlesRef.current[lastCandlesRef.current.length - 1].close : 0}
          colorTheme={colorTheme}
          onClose={() => setIsAlertPanelOpen(false)}
        />
      )}

      {panel.error && (
        <div className="chart-error-banner">
          <span title={panel.error}>⚠ {panel.error}</span>
          <button onClick={() => triggerFetch(panel.symbol)}>Retry</button>
        </div>
      )}

      <div className="chart-body">
        <div ref={chartContainerRef} className={`chart-container${!panel.symbol ? " chart-container-hidden" : ""}`}>
          {panel.symbol && (
            <div className="chart-ohlc-legend">
            {(() => {
              // Show hovered candle data, or fall back to latest candle
              const d = legendData || (lastCandlesRef.current.length > 0 ? (() => {
                const last = lastCandlesRef.current[lastCandlesRef.current.length - 1];
                return {
                  time: last.time as number,
                  open: last.open,
                  high: last.high,
                  low: last.low,
                  close: last.close,
                  volume: last.volume ?? 0,
                  oi: (last as any).oi ?? 0,
                  change: last.close - last.open,
                };
              })() : null);

              if (!d) return null;

              const isUp = d.change >= 0;
              const colorClass = isUp ? "legend-up" : "legend-down";
              // Format candle date/time — IST timestamp to "DD-MM-YYYY HH:mm"
              const fmtDateTime = (ts: number) => {
                const date = new Date(ts * 1000);
                const dd = String(date.getUTCDate()).padStart(2, '0');
                const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
                const yyyy = date.getUTCFullYear();
                const hh = String(date.getUTCHours()).padStart(2, '0');
                const min = String(date.getUTCMinutes()).padStart(2, '0');
                return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
              };
              const fmt = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const fmtVol = (n: number) => {
                if (n >= 10000000) return (n / 10000000).toFixed(2) + "Cr";
                if (n >= 100000) return (n / 100000).toFixed(2) + "L";
                if (n >= 1000) return (n / 1000).toFixed(2) + "K";
                return n.toString();
              };

              // Today's open: find the first candle of the current IST trading day.
              // Candle timestamps are IST-aligned (UTC getters give IST values).
              // Current IST date: shift Date.now() by IST_OFFSET so UTC getters reflect IST.
              const todayOpen = (() => {
                const candles = lastCandlesRef.current;
                if (candles.length === 0) return null;
                const nowIST = new Date(Date.now() + IST_OFFSET * 1000);
                const tD = nowIST.getUTCDate();
                const tM = nowIST.getUTCMonth();
                const tY = nowIST.getUTCFullYear();
                const first = candles.find(c => {
                  const cd = new Date((c.time as number) * 1000);
                  return cd.getUTCDate() === tD && cd.getUTCMonth() === tM && cd.getUTCFullYear() === tY;
                });
                return first ? first.open : null;
              })();

              // Point & percentage change from today's open
              const dayChange = todayOpen !== null ? d.close - todayOpen : null;
              const dayChangePct = (dayChange !== null && todayOpen) ? (dayChange / todayOpen) * 100 : null;
              const fmtChg = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
              const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
              const changeClass = dayChange !== null && dayChange >= 0 ? "legend-up" : "legend-down";

              return (
                <>
                  <div className="legend-row">
                    <span className="legend-datetime">{fmtDateTime(d.time)}</span>
                  </div>
                  <span className="legend-sep">·</span>
                  <div className="legend-row">
                    <span className="legend-label">O</span>
                    <span className={colorClass}>{fmt(d.open)}</span>
                    <span className="legend-label">H</span>
                    <span className={colorClass}>{fmt(d.high)}</span>
                    <span className="legend-label">L</span>
                    <span className={colorClass}>{fmt(d.low)}</span>
                    <span className="legend-label">C</span>
                    <span className={colorClass}>{fmt(d.close)}</span>
                  </div>
                  <span className="legend-sep">·</span>
                  <div className="legend-row">
                    <span className="legend-label">Vol</span>
                    <span className="legend-value">{fmtVol(d.volume)}</span>
                    {d.oi > 0 && (
                      <>
                        <span className="legend-label">OI</span>
                        <span className="legend-value">{fmtVol(d.oi)}</span>
                      </>
                    )}
                    {dayChange !== null && dayChangePct !== null && (
                      <span className={`legend-day-change ${changeClass}`}>
                        {fmtChg(dayChange)}&nbsp;({fmtPct(dayChangePct)})
                      </span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
        {panel.symbol && chartRef.current && candleSeriesRef.current && (
          <DrawingOverlay
            chart={chartRef.current}
            candleSeries={candleSeriesRef.current}
            drawings={drawings}
            activeTool={activeTool}
            activeColor={activeDrawingColor}
            istOffset={IST_OFFSET}
            drawingsVisible={drawingsVisible}
            priceSnapTick={inferredPriceTick}
            timeframeSeconds={(getTimeframeConfig(panel.timeframe)?.minutes ?? 1) * 60}
            onDrawingComplete={handleDrawingComplete}
            onDrawingUpdate={handleDrawingUpdate}
            onDrawingDelete={handleDrawingDelete}
            colorTheme={colorTheme}
          />
        )}
        {/* Alpha Trade Dashboard Overlay */}
        {alphaTradeActive && panel.symbol && (
          <AlphaTradeOverlay
            dashboard={alphaTradeData}
            symbol={panel.symbol.symbol}
            visible={alphaTradeActive}
            showDashboard={atDashboardVisible}
            onToggleDashboard={() => setAtDashboardVisible(v => !v)}
            arrowBadges={atArrowBadges}
          />
        )}
        {/* Profit Machine Dashboard Overlay */}
        {profitMachineActive && panel.symbol && (
          <ProfitMachineOverlay
            dashboard={profitMachineData}
            symbol={panel.symbol.symbol}
            visible={profitMachineActive}
            showDashboard={pmDashboardVisible}
            onToggleDashboard={() => setPmDashboardVisible(v => !v)}
            arrowBadges={pmArrowBadges}
          />
        )}
        {/* Scalping Trend Dashboard Overlay */}
        {scalpingTrendActive && panel.symbol && (
          <ScalpingTrendOverlay
            dashboard={scalpingTrendData}
            symbol={panel.symbol.symbol}
            visible={scalpingTrendActive}
            showDashboard={stDashboardVisible}
            onToggleDashboard={() => setStDashboardVisible(v => !v)}
            arrowBadges={stArrowBadges}
          />
        )}
        {/* Prime Magic Dashboard Overlay */}
        {primeMagicActive && panel.symbol && (
          <PrimeMagicOverlay
            dashboard={primeMagicData}
            symbol={panel.symbol.symbol}
            visible={primeMagicActive}
            showDashboard={pmgDashboardVisible}
            onToggleDashboard={() => setPmgDashboardVisible(v => !v)}
            arrowBadges={pmgArrowBadges}
          />
        )}
        {/* Max Wealth Dashboard Overlay */}
        {maxWealthActive && panel.symbol && (
          <MaxWealthOverlay
            dashboard={maxWealthData}
            symbol={panel.symbol.symbol}
            visible={maxWealthActive}
            showDashboard={mwDashboardVisible}
            onToggleDashboard={() => setMwDashboardVisible(v => !v)}
            arrowBadges={mwArrowBadges}
          />
        )}
        {/* Shadow Breaker Dashboard Overlay */}
        {shadowBreakerActive && panel.symbol && (
          <ShadowBreakerOverlay
            dashboard={shadowBreakerData}
            symbol={panel.symbol.symbol}
            visible={shadowBreakerActive}
            showDashboard={sbDashboardVisible}
            onToggleDashboard={() => setSbDashboardVisible(v => !v)}
            arrowBadges={sbArrowBadges}
          />
        )}
        {/* Swing Trade Dashboard Overlay */}
        {swingTradeActive && panel.symbol && (
          <SwingTradeOverlay
            dashboard={swingTradeData}
            symbol={panel.symbol.symbol}
            visible={swingTradeActive}
            showDashboard={swDashboardVisible}
            onToggleDashboard={() => setSwDashboardVisible(v => !v)}
            arrowBadges={swArrowBadges}
          />
        )}
        {/* Trend Rover Dashboard Overlay */}
        {trendRoverActive && panel.symbol && (
          <TrendRoverOverlay
            dashboard={trendRoverData}
            symbol={panel.symbol.symbol}
            visible={trendRoverActive}
            showDashboard={trDashboardVisible}
            onToggleDashboard={() => setTrDashboardVisible(v => !v)}
            arrowBadges={trArrowBadges}
          />
        )}
        {/* Trend Monster Dashboard Overlay */}
        {trendMonsterActive && panel.symbol && (
          <TrendMonsterOverlay
            dashboard={trendMonsterData}
            symbol={panel.symbol.symbol}
            visible={trendMonsterActive}
            showDashboard={tmnDashboardVisible}
            onToggleDashboard={() => setTmnDashboardVisible(v => !v)}
            arrowBadges={tmnArrowBadges}
          />
        )}
        {/* Trade Mint Dashboard Overlay */}
        {tradeMintActive && panel.symbol && (
          <TradeMintOverlay
            dashboard={tradeMintData}
            symbol={panel.symbol.symbol}
            visible={tradeMintActive}
            showDashboard={tmDashboardVisible}
            onToggleDashboard={() => setTmDashboardVisible(v => !v)}
            arrowBadges={tmArrowBadges}
          />
        )}
        {/* Price Action Dashboard Overlay */}
        {priceActionActive && panel.symbol && (
          <PriceActionOverlay
            dashboard={priceActionData}
            symbol={panel.symbol.symbol}
            visible={priceActionActive}
            showDashboard={paDashboardVisible}
            onToggleDashboard={() => setPaDashboardVisible(v => !v)}
            arrowBadges={paArrowBadges}
          />
        )}
        {/* Alpha Trade V2 Dashboard Overlay */}
        {alphaTradeV2Active && panel.symbol && (
          <AlphaTradeV2Overlay
            dashboard={alphaTradeV2Data}
            symbol={panel.symbol.symbol}
            visible={alphaTradeV2Active}
            showDashboard={atv2DashboardVisible}
            onToggleDashboard={() => setAtv2DashboardVisible(v => !v)}
            arrowBadges={atv2ArrowBadges}
          />
        )}

          {/* Active Indicators Bar — bottom-left overlay */}
          {showActiveIndicators && (
            <ActiveIndicatorsBar
              activeIndicators={activeIndicators}
              onRemove={handleRemoveIndicator}
              onToggleVisibility={handleToggleIndicatorVisibility}
              onParamsChange={handleIndicatorParamsChange}
              colorTheme={colorTheme}
            />
          )}
        </div>

        {!panel.symbol && (
          <div className="chart-empty-welcome">
            {watchlistScrips.length > 0 ? (
              <div className="ew-grid">
                {watchlistScrips.map((scrip) => {
                  const ltp = scrip.ltp ?? 0;
                  const dir = scrip.ltpDirection;
                  return (
                    <div
                      key={scrip.id}
                      className={`ew-card ${dir === 'up' ? 'card-up' : dir === 'down' ? 'card-down' : ''}`}
                      onClick={() => {
                        const sym: ChartSymbol = { symbol: scrip.symbol, segment: scrip.segment, name: scrip.symbol, id: scrip.id };
                        dispatch(setSymbol({ panelId, symbol: sym }));
                        dispatch(setActiveTab(panelId));
                      }}
                    >
                      <span className="ew-card-symbol">{scrip.symbol}</span>
                      <div className="ew-card-bottom">
                        <span className={`ew-card-ltp ${dir === 'up' ? 'ltp-up' : dir === 'down' ? 'ltp-down' : ''}`}>
                          {ltp > 0 ? (
                            <>
                              {dir === 'up' ? (
                                <svg viewBox="0 0 10 10" width="10" height="10" fill="currentColor" className="ltp-arrow"><path d="M5 1 L9 9 L1 9 Z"/></svg>
                              ) : dir === 'down' ? (
                                <svg viewBox="0 0 10 10" width="10" height="10" fill="currentColor" className="ltp-arrow"><path d="M5 9 L9 1 L1 1 Z"/></svg>
                              ) : null}
                              {ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </>
                          ) : '—'}
                        </span>
                        <span className="ew-card-btn">
                          <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="3" width="4" height="14" rx="1" /><rect x="8" y="7" width="4" height="10" rx="1" /><rect x="14" y="1" width="4" height="16" rx="1" />
                          </svg>
                          Open Chart
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ew-empty-state">
                <h2 className="ew-title">No Symbols in Watchlist</h2>
                <p className="ew-subtitle">Your market watch is empty. Add symbols to begin tracking live prices and open charts.</p>
                <button className="ew-add-btn" onClick={() => setAddSymbolModalOpen(true)}>
                  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="10" y1="4" x2="10" y2="16" /><line x1="4" y1="10" x2="16" y2="10" />
                  </svg>
                  Add Symbols
                </button>
              </div>
            )}
            <MarketSegmentModal
              addMarketSegmentModalIsOpen={addSymbolModalOpen}
              setAddMarketSegmentModalIsOpen={setAddSymbolModalOpen}
            />
          </div>
        )}
      </div>

      {/* Strategy Editor & Backtest Panel */}
      {isStrategyOpen && panel.symbol && (
        <StrategyPanel
          candles={convertedCandles}
          symbol={panel.symbol.symbol}
          timeframe={panel.timeframe}
          colorTheme={colorTheme}
          onClose={() => setIsStrategyOpen(false)}
        />
      )}

      {/* Custom Indicator Scripting Panel */}
      {isCustomIndicatorOpen && (
        <CustomIndicatorPanel
          colorTheme={colorTheme}
          onClose={() => setIsCustomIndicatorOpen(false)}
          onApply={handleApplyCustomIndicator}
        />
      )}

      {/* Bar Replay Controls */}
      {isReplayMode && (
        <BarReplayPanel
          totalBars={replayAllCandlesRef.current.length}
          isPlaying={replayPlaying}
          currentBar={replayBar}
          speed={replaySpeed}
          onPlay={replayPlay}
          onPause={replayPause}
          onStep={replayStepForward}
          onStepBack={replayStepBack}
          onReset={replayReset}
          onSeek={replaySeek}
          onSpeedChange={replaySpeedChange}
          onExit={replayExit}
          colorTheme={colorTheme}
        />
      )}

      {/* Compare Symbol Overlay Panel */}
      {isCompareOpen && (
        <CompareSymbolPanel
          entries={compareEntries}
          panelSymbol={panel.symbol}
          availableSymbols={availableSymbols}
          colorTheme={colorTheme}
          onAdd={handleCompareAdd}
          onRemove={handleCompareRemove}
          onClose={() => setIsCompareOpen(false)}
        />
      )}

      {/* Transparent drop overlay — shown on top of canvas/SVG only when a
          chart-symbol drag is in progress. Guarantees reliable drop events
          regardless of what's underneath (canvas, SVG, DrawingOverlay). */}
      {isSymbolDragActive && (
        <div
          className={`chart-drop-overlay${isDragOver ? " active" : ""}`}
          onDragEnter={handleOverlayDragEnter}
          onDragOver={handleOverlayDragOver}
          onDragLeave={handleOverlayDragLeave}
          onDrop={handleOverlayDrop}
        />
      )}
    </div>
  );
}));

ChartPanel.displayName = "ChartPanel";

export default ChartPanel;
